const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1';

interface TokenResponse {
  access: string;
  refresh: string;
  user: User;
}

export interface User {
  id: string;
  username: string;
  email: string;
  first_name: string;
  last_name: string;
  full_name: string;
  role: string;
}

export interface Client {
  id: string;
  name: string;
  company: string;
  industry: string | null;
  industry_name: string;
  industry_display: string;
  email: string;
  phone: string;
  location: string;
  status: 'internal' | 'active' | 'pending' | 'completed' | 'inactive';
  account_manager: string | null;
  account_manager_name: string | null;
  notes: string;
  total_spent: string;
  contract_amount: string;
  billing_enabled: boolean;
  billing_status: 'unbilled' | 'invoiced' | 'paid' | 'subscription_active' | 'subscription_past_due' | 'subscription_cancelled';
  stripe_customer_id: string | null;
  stripe_subscription_id: string | null;
  phase_status: 'pending' | 'active' | 'suspended';
  phase: 'initial' | 'cruising';
  google_exposure_model: '' | 'sab_single' | 'sab_multi' | 'brand';
  scale_pattern: '' | 'single_market' | 'multi_market_finite' | 'multi_market_programmatic';
  demand_modes: ('maps' | 'organic' | 'ai' | 'ads')[];
  gbp_count: number;
  active_projects_count: number;
  completed_projects_count: number;
  created_at: string;
  updated_at: string;
  projects?: Project[];
  services?: ClientService[];
  markets?: Market[];
  findings?: ClientFinding[];
  sop_packs?: string[];
  expected_city_pages_count?: number;
  markets_count?: number;
}

export interface Market {
  id: string;
  client: string;
  city: string;
  region: string;
  zip_codes: string[];
  city_page_url: string;
  is_primary: boolean;
  gbp_locations_count: number;
  created_at: string;
  updated_at: string;
}

export interface LandingPageConfig {
  id: string;
  client: string;
  client_name: string;
  subdomain: string;
  business_name: string;
  tagline: string;
  phone: string;
  phone_display: string;
  email: string;
  services: string[];
  service_areas: string[];
  cta_text: string;
  primary_color: string;
  primary_color_dark: string;
  is_published: boolean;
  last_built_at: string | null;
  build_status: 'pending' | 'building' | 'success' | 'failed';
  build_error: string;
  created_at: string;
  updated_at: string;
}

export interface LandingPageInput {
  subdomain: string;
  business_name: string;
  tagline: string;
  phone: string;
  phone_display: string;
  email?: string;
  services: string[];
  service_areas: string[];
  cta_text: string;
  primary_color: string;
  primary_color_dark: string;
}

export interface TrackingNumber {
  id: string;
  client: string;
  phone_e164: string;
  phone_display: string;
  purpose: 'main' | 'ads' | 'landing' | 'gbp' | 'referral';
  forward_to: string;
  is_active: boolean;
  plivo_number_id: string;
  total_calls: number;
  total_sms: number;
  created_at: string;
  updated_at: string;
}

export interface CallLog {
  id: string;
  lead_id: string | null;
  caller: string;
  caller_name: string | null;
  tracking_number: string;
  started_at: string | null;
  duration: number | null;
  status: 'answered' | 'missed' | 'voicemail' | 'unknown';
  recording_url: string | null;
  voicemail_url: string | null;
}

export interface CallLogStats {
  total: number;
  answered: number;
  missed: number;
  voicemails: number;
  total_duration: number;
  avg_duration: number;
}

export interface CallLogResponse {
  calls: CallLog[];
  stats: CallLogStats;
}

export interface ClientNotificationContact {
  id: string;
  client: string;
  name: string;
  phone: string;
  email: string;
  notify_via: 'sms' | 'email' | 'both';
  is_primary: boolean;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

// Onboarding types
export interface OnboardingModuleChecklist {
  required: boolean;
  name: string;
  items: Record<string, boolean>;
}

export interface OnboardingStep {
  status: 'pending' | 'in_progress' | 'complete';
  completed_at?: string;
  completed_by?: string;
  checklist: Record<string, boolean | OnboardingModuleChecklist>;
}

export interface ClientOnboarding {
  id: string;
  client: string;
  client_name: string;
  status: 'not_started' | 'in_progress' | 'completed';
  started_at: string | null;
  completed_at: string | null;
  current_step: number;
  is_complete: boolean;
  progress_percentage: number;
  modules_sold: string[];
  steps: Record<string, OnboardingStep>;
  validation_results: ValidationResults | Record<string, never>;
  handoff_email_sent_at: string | null;
  client_confirmed_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface ValidationCheckResult {
  passed: boolean;
  detail: string;
}

export interface ValidationResults {
  all_passed: boolean;
  checks: Record<string, ValidationCheckResult>;
}

// Billing types
export interface Engagement {
  id: string;
  client: string;
  client_name: string;
  engagement_type: 'initial' | 'cruising';
  status: 'draft' | 'sent' | 'paid' | 'cancelled' | 'refunded';
  amount_cents: number;
  amount_dollars: number;
  currency: string;
  description: string;
  stripe_invoice_id: string | null;
  stripe_checkout_session_id: string | null;
  stripe_payment_intent_id: string | null;
  paid_at: string | null;
  cancelled_at: string | null;
  metadata: Record<string, unknown>;
  is_paid: boolean;
  created_at: string;
  updated_at: string;
}

export interface BillingSummary {
  billing_status: string;
  billing_enabled: boolean;
  total_spent: string;
  contract_amount: string;
  balance_due: string;
  stripe_customer_id: string | null;
  stripe_subscription_id: string | null;
  phase_status: string;
  engagements: Engagement[];
}

export interface ClientFinding {
  id: string;
  client: string;
  severity: 'info' | 'warn' | 'critical';
  check_type: string;
  message: string;
  related_object_id: string | null;
  is_resolved: boolean;
  resolved_at: string | null;
  created_at: string;
}

export interface Project {
  id: string;
  name: string;
  client: string;
  client_name: string;
  project_type: 'AI' | 'Marketing';
  status: 'active' | 'paused' | 'completed' | 'cancelled';
  description: string;
  progress: number;
  budget: string | null;
  deadline: string | null;
  team_count: number;
  created_at: string;
  updated_at: string;
}

export interface Service {
  id: string;
  name: string;
  description: string;
  service_type: 'AI' | 'Marketing';
  base_price: string | null;
  is_active: boolean;
  created_at: string;
}

export interface ClientService {
  id: string;
  service: string;
  service_name: string;
  service_type: 'AI' | 'Marketing';
  start_date: string | null;
  end_date: string | null;
  is_active: boolean;
  price: string | null;
}

export interface Campaign {
  id: string;
  name: string;
  campaign_type: string;
  status: 'draft' | 'scheduled' | 'active' | 'paused' | 'completed';
  description: string;
  client: string | null;
  client_name: string | null;
  start_date: string | null;
  end_date: string | null;
  budget: string | null;
  target_reach: number;
  actual_reach: number;
  engagement_rate: string;
  conversions: number;
  roi: string;
  tags: string[];
  team_count: number;
  created_at: string;
  updated_at: string;
}

export interface DashboardStats {
  clients: {
    total: number;
    active: number;
    pending: number;
    total_revenue: string | null;
  };
  projects: {
    total: number;
    active: number;
    completed: number;
  };
  campaigns: {
    total: number;
    active: number;
    total_reach: number | null;
    total_conversions: number | null;
  };
  pipeline: {
    total: number;
    leads: number;
    qualified: number;
    proposals: number;
    negotiation: number;
    won: number;
    lost: number;
    total_value: string | null;
  };
}

export interface TeamMember {
  id: string;
  username: string;
  email: string;
  first_name: string;
  last_name: string;
  full_name: string;
  role: string;
  phone_extension: string | null;
  skills: string[];
  is_active_agent: boolean;
  active_projects: number;
  projects_completed: number;
  created_at: string;
}

export interface Transaction {
  id: string;
  transaction_type: 'income' | 'expense';
  category: string;
  amount: string;
  description: string;
  client: string | null;
  client_name: string | null;
  project: string | null;
  project_name: string | null;
  status: 'completed' | 'pending' | 'failed';
  transaction_date: string;
  created_at: string;
  updated_at: string;
}

export interface FinanceStats {
  total_revenue: number;
  total_expenses: number;
  net_profit: number;
  pending_payments: number;
  transaction_count?: number;
  total_balance?: number;
  accounts?: MercuryAccount[];
  period_start?: string;
  period_end?: string;
}

export interface MercuryAccount {
  id: string;
  name: string;
  type: string;
  balance: number;
  available_balance: number;
}

export interface MercuryTransaction {
  id: string;
  mercury_id: string;
  transaction_type: 'income' | 'expense';
  category: string;
  amount: string;
  description: string;
  counterparty_name: string;
  status: 'completed' | 'pending' | 'failed';
  transaction_date: string;
  account_name: string;
  account_id: string;
  created_at: string;
  details?: Record<string, unknown>;
}

// Google Ads Interfaces
export interface GoogleAdsAccount {
  id: string;
  client: string;
  client_name: string | null;
  customer_id: string;
  name: string;
  currency_code: string;
  timezone: string;
  total_spend: string;
  total_impressions: number;
  total_clicks: number;
  total_conversions: number;
  average_cpc: string;
  average_ctr: string;
  is_active: boolean;
  last_synced_at: string | null;
  campaigns_count: number;
  created_at: string;
  updated_at: string;
}

export interface GoogleAdsCampaign {
  id: string;
  account: string;
  account_name: string | null;
  client_name: string | null;
  campaign_id: string;
  name: string;
  campaign_type: 'search' | 'display' | 'shopping' | 'video' | 'app' | 'smart' | 'performance_max';
  status: 'enabled' | 'paused' | 'removed';
  daily_budget: string;
  total_budget: string | null;
  bidding_strategy: 'manual_cpc' | 'maximize_clicks' | 'maximize_conversions' | 'target_cpa' | 'target_roas';
  target_cpa: string | null;
  target_roas: string | null;
  target_locations: string[];
  target_languages: string[];
  start_date: string | null;
  end_date: string | null;
  impressions: number;
  clicks: number;
  cost: string;
  conversions: number;
  conversion_value: string;
  ctr: string;
  average_cpc: string;
  ad_groups_count: number;
  created_at: string;
  updated_at: string;
}

export interface GoogleAdsAdGroup {
  id: string;
  campaign: string;
  campaign_name: string | null;
  ad_group_id: string;
  name: string;
  status: 'enabled' | 'paused' | 'removed';
  cpc_bid: string | null;
  impressions: number;
  clicks: number;
  cost: string;
  conversions: number;
  ctr: string;
  average_cpc: string;
  ads_count: number;
  keywords_count: number;
  created_at: string;
  updated_at: string;
}

export interface GoogleAdsAd {
  id: string;
  ad_group: string;
  ad_group_name: string | null;
  campaign_name: string | null;
  ad_id: string;
  ad_type: 'responsive_search' | 'expanded_text' | 'responsive_display' | 'image' | 'video';
  status: 'enabled' | 'paused' | 'removed';
  approval_status: 'approved' | 'approved_limited' | 'disapproved' | 'under_review';
  headlines: string[];
  descriptions: string[];
  final_urls: string[];
  display_url: string;
  impressions: number;
  clicks: number;
  cost: string;
  conversions: number;
  ctr: string;
  created_at: string;
  updated_at: string;
}

export interface GoogleAdsKeyword {
  id: string;
  ad_group: string;
  ad_group_name: string | null;
  campaign_name: string | null;
  keyword_id: string;
  text: string;
  match_type: 'broad' | 'phrase' | 'exact';
  status: 'enabled' | 'paused' | 'removed';
  cpc_bid: string | null;
  quality_score: number | null;
  expected_ctr: string;
  ad_relevance: string;
  landing_page_exp: string;
  impressions: number;
  clicks: number;
  cost: string;
  conversions: number;
  ctr: string;
  average_cpc: string;
  average_position: string | null;
  created_at: string;
  updated_at: string;
}

export interface GoogleAdsDashboard {
  accounts: {
    total: number;
    total_spend: number;
    total_impressions: number;
    total_clicks: number;
    total_conversions: number;
  };
  campaigns: {
    total: number;
    enabled: number;
    paused: number;
  };
  recent_campaigns: GoogleAdsCampaign[];
  accounts_list: GoogleAdsAccount[];
}

export interface GoogleAdsAccountSummary {
  period_start: string;
  period_end: string;
  total_impressions: number;
  total_clicks: number;
  total_spend: number;
  total_conversions: number;
  total_conversion_value: number;
  average_ctr: number;
  average_cpc: number;
}

export interface GoogleAdsDailyData {
  date: string;
  impressions: number;
  clicks: number;
  cost: number;
  conversions: number;
  ctr: number;
  average_cpc: number;
}

export interface GoogleAdsOAuthResponse {
  access_token: string;
  refresh_token: string;
  expires_in: number;
  accounts: Array<{
    customer_id: string;
    name: string;
    currency_code: string;
    timezone: string;
  }>;
}

// Google Ads Advanced Analytics Interfaces
export interface GoogleAdsSearchTerm {
  search_term: string;
  keyword_text: string;
  match_type: string;
  campaign_name: string;
  ad_group_name: string;
  impressions: number;
  clicks: number;
  cost: number;
  conversions: number;
  ctr: number;
  average_cpc: number;
}

export interface GoogleAdsConversionAction {
  id: string;
  name: string;
  category: string;
  origin: string;
  status: string;
  type: string;
  counting_type: string;
  value_settings: {
    default_value: number;
    always_use_default: boolean;
  };
  attribution_model: string;
  include_in_conversions: boolean;
}

export interface GoogleAdsConversionMetric {
  conversion_action: string;
  conversions: number;
  conversion_value: number;
  cost_per_conversion: number;
  conversion_rate: number;
  value_per_conversion: number;
}

export interface GoogleAdsDevicePerformance {
  device: string;
  impressions: number;
  clicks: number;
  cost: number;
  conversions: number;
  ctr: number;
  average_cpc: number;
  conversion_rate: number;
}

export interface GoogleAdsGeoPerformance {
  country: string;
  region: string;
  city: string;
  impressions: number;
  clicks: number;
  cost: number;
  conversions: number;
  ctr: number;
  average_cpc: number;
}

export interface GoogleAdsDayOfWeekPerformance {
  day_of_week: string;
  impressions: number;
  clicks: number;
  cost: number;
  conversions: number;
  ctr: number;
  average_cpc: number;
}

export interface GoogleAdsHourPerformance {
  hour: number;
  impressions: number;
  clicks: number;
  cost: number;
  conversions: number;
  ctr: number;
  average_cpc: number;
}

export interface GoogleAdsImpressionShare {
  campaign_name: string;
  search_impression_share: number;
  search_top_impression_share: number;
  search_absolute_top_impression_share: number;
  search_budget_lost_impression_share: number;
  search_rank_lost_impression_share: number;
  content_impression_share: number;
  content_budget_lost_impression_share: number;
  content_rank_lost_impression_share: number;
}

export interface GoogleAdsAuctionInsight {
  display_domain: string;
  impression_share: number;
  overlap_rate: number;
  position_above_rate: number;
  top_of_page_rate: number;
  abs_top_of_page_rate: number;
  outranking_share: number;
}

export interface GoogleAdsOptimizationAudit {
  summary: {
    total_impressions: number;
    total_clicks: number;
    total_cost: number;
    total_conversions: number;
    average_ctr: number;
    average_cpc: number;
    conversion_rate: number;
  };
  issues: Array<{
    severity: string;
    category: string;
    title: string;
    description: string;
    recommendation: string;
    impact: string;
  }>;
  opportunities: Array<{
    category: string;
    title: string;
    description: string;
    potential_impact: string;
  }>;
  scores: {
    overall: number;
    budget_utilization: number;
    targeting: number;
    ad_quality: number;
    keyword_health: number;
  };
}

// Google Business Profile Types
export interface GBPOAuthAccount {
  name: string;  // Resource name like accounts/123
  account_name: string;  // Display name
  type: string;
  role: string;
  verification_state: string;
}

// Unified Google OAuth Response
// Managed account (sub-account) for Manager/MCC accounts
export interface ManagedAccount {
  customer_id: string;
  name: string;
  currency_code?: string;
  timezone?: string;
  is_manager?: boolean;
  is_test_account?: boolean;
}

export interface GoogleOAuthCallbackResponse {
  service: 'google_ads' | 'gbp' | 'gsc' | 'facebook_ads';
  access_token: string;
  refresh_token?: string;
  expires_in: number;
  token_type?: string;
  accounts: Array<{
    // Google Ads fields
    customer_id?: string;
    currency_code?: string;
    timezone?: string;
    // Manager/MCC account fields
    account_type?: 'manager' | 'client';
    is_manager?: boolean;
    is_test_account?: boolean;
    managed_accounts?: ManagedAccount[];
    // GBP fields
    name?: string;
    account_name?: string;
    type?: string;
    role?: string;
    verification_state?: string;
    // GBP auto-refresh fields (for existing accounts)
    existing?: boolean;
    client_id?: string;
    client_name?: string;
    refreshed?: boolean;
    // Facebook Ads fields
    account_id?: string;
    currency?: string;
    timezone_name?: string;
    account_status?: number;
    business_id?: string;
    relationship?: string;
  }>;
  // GSC-specific: list of sites/properties
  sites?: GSCOAuthSite[];
  // Facebook Ads-specific: list of business managers
  businesses?: Array<{
    business_id: string;
    name: string;
    verification_status?: string;
  }>;
  // GBP auto-refresh stats
  auto_refreshed?: number;
  new_accounts?: number;
}

// Google Search Console OAuth Site
export interface GSCOAuthSite {
  site_url: string;
  permission_level: string;
}

// Facebook Ads Interfaces
export interface FacebookAdsAccount {
  id: string;
  client: string;
  client_name: string | null;
  account_id: string;
  name: string;
  currency: string;
  timezone_name: string;
  account_status: number;
  business_id: string | null;
  token_type: string;
  token_status: string;
  total_spend: number;
  total_impressions: number;
  total_clicks: number;
  total_conversions: number;
  total_reach: number;
  average_cpc: number;
  average_cpm: number;
  average_ctr: number;
  is_active: boolean;
  last_synced_at: string | null;
  campaigns_count: number;
  created_at: string;
  updated_at: string;
}

export interface FacebookAdsCampaign {
  id: string;
  account: string;
  account_name: string | null;
  client_name: string | null;
  campaign_id: string;
  name: string;
  objective: string;
  status: string;
  effective_status: string;
  daily_budget: number | null;
  lifetime_budget: number | null;
  budget_remaining: number | null;
  bid_strategy: string;
  spend_cap: number | null;
  special_ad_categories: string[];
  start_time: string | null;
  stop_time: string | null;
  impressions: number;
  clicks: number;
  spend: number;
  reach: number;
  conversions: number;
  conversion_value: number;
  ctr: number;
  cpc: number;
  cpm: number;
  frequency: number;
  ad_sets_count: number;
  created_at: string;
  updated_at: string;
}

export interface FacebookAdsAdSet {
  id: string;
  campaign: string;
  campaign_name: string | null;
  adset_id: string;
  name: string;
  status: string;
  effective_status: string;
  daily_budget: number | null;
  lifetime_budget: number | null;
  budget_remaining: number | null;
  bid_amount: number | null;
  bid_strategy: string;
  billing_event: string;
  optimization_goal: string;
  targeting: Record<string, unknown>;
  start_time: string | null;
  end_time: string | null;
  learning_stage_info: Record<string, unknown>;
  impressions: number;
  clicks: number;
  spend: number;
  reach: number;
  conversions: number;
  ctr: number;
  cpc: number;
  ads_count: number;
  created_at: string;
  updated_at: string;
}

export interface FacebookAdsAd {
  id: string;
  ad_set: string;
  adset_name: string | null;
  campaign_name: string | null;
  ad_id: string;
  name: string;
  status: string;
  effective_status: string;
  creative_id: string;
  preview_shareable_link: string;
  tracking_specs: unknown[];
  review_feedback: string;
  issues_info: unknown[];
  impressions: number;
  clicks: number;
  spend: number;
  reach: number;
  conversions: number;
  ctr: number;
  cpc: number;
  created_at: string;
  updated_at: string;
}

export interface FacebookAdsDashboard {
  accounts: {
    total: number;
    total_spend: number;
    total_impressions: number;
    total_clicks: number;
    total_conversions: number;
    total_reach: number;
  };
  campaigns: {
    total: number;
    active: number;
    paused: number;
  };
  recent_campaigns: FacebookAdsCampaign[];
  accounts_list: FacebookAdsAccount[];
}

export interface FacebookAdsAnalytics {
  summary: {
    period_start: string;
    period_end: string;
    impressions: number;
    clicks: number;
    spend: number;
    reach: number;
    ctr: number;
    cpc: number;
    cpm: number;
    frequency: number;
    conversions: number;
    conversion_value: number;
  };
  daily_data: Array<{
    date: string;
    impressions: number;
    clicks: number;
    spend: number;
    reach: number;
    ctr: number;
    cpc: number;
    cpm: number;
  }>;
}

export interface GBPAccount {
  id: string;
  client: string;
  client_name: string | null;
  account_name: string;
  display_name: string;
  account_type: string;
  role: string;
  is_active: boolean;
  last_synced_at: string | null;
  locations_count: number;
  created_at: string;
  updated_at: string;
}

export interface GBPLocation {
  id: string;
  account: string;
  account_name: string | null;
  client_name: string | null;
  location_name: string;
  name: string;  // display_name or locality fallback
  title: string;
  address_lines: string[];
  locality: string;
  region: string;
  postal_code: string;
  country: string;
  full_address: string;
  phone: string;
  website: string;
  latitude: number | null;
  longitude: number | null;
  place_id: string;
  maps_uri: string;
  primary_category_name: string;
  primary_category_display: string;
  additional_categories: Array<{ name: string; display_name: string }>;
  description: string;
  labels: string[];
  regular_hours: Array<{ day: string; open_time: number; close_time: number }>;
  total_reviews: number;
  average_rating: number;
  total_views: number;
  total_searches: number;
  website_clicks: number;
  direction_requests: number;
  phone_calls: number;
  is_verified: boolean;
  last_synced_at: string | null;
  reviews_count: number;
  posts_count: number;
  photos_count: number;
  needs_reply_count: number;
  created_at: string;
  updated_at: string;
}

export interface GBPReview {
  id: string;
  location: string;
  location_title: string | null;
  review_name: string;
  review_id: string;
  reviewer_name: string;
  reviewer_photo_url: string;
  star_rating: 'ONE' | 'TWO' | 'THREE' | 'FOUR' | 'FIVE';
  star_rating_int: number;
  comment: string;
  review_time: string | null;
  update_time: string | null;
  reply_comment: string;
  reply_time: string | null;
  has_reply: boolean;
  created_at: string;
  updated_at: string;
}

export interface GBPPost {
  id: string;
  location: string;
  location_title: string | null;
  post_name: string;
  summary: string;
  topic_type: 'STANDARD' | 'EVENT' | 'OFFER' | 'ALERT';
  state: string;
  language_code: string;
  media: Array<{ media_format: string; source_url: string; google_url: string }>;
  cta_type: string;
  cta_url: string;
  event_title: string;
  event_start: string | null;
  event_end: string | null;
  offer_coupon_code: string;
  offer_redeem_url: string;
  offer_terms: string;
  search_url: string;
  post_time: string | null;
  created_at: string;
  updated_at: string;
}

export interface GBPPhoto {
  id: string;
  location: string;
  location_title: string | null;
  photo_name: string;
  media_format: 'PHOTO' | 'VIDEO';
  category: string;
  source_url: string;
  google_url: string;
  thumbnail_url: string;
  width: number;
  height: number;
  description: string;
  view_count: number;
  created_at: string;
  updated_at: string;
}

export interface GBPQuestion {
  id: string;
  location: string;
  location_title: string | null;
  question_name: string;
  author_name: string;
  author_photo_url: string;
  author_type: string;
  text: string;
  question_time: string | null;
  update_time: string | null;
  upvote_count: number;
  total_answer_count: number;
  answers: GBPAnswer[];
  created_at: string;
  updated_at: string;
}

export interface GBPAnswer {
  id: string;
  question: string;
  answer_name: string;
  author_name: string;
  author_photo_url: string;
  author_type: string;
  text: string;
  answer_time: string | null;
  update_time: string | null;
  upvote_count: number;
  created_at: string;
  updated_at: string;
}

export interface GBPInsights {
  period_start: string;
  period_end: string;
  total_views: number;
  search_views: number;
  maps_views: number;
  desktop_views: number;
  mobile_views: number;
  website_clicks: number;
  direction_requests: number;
  phone_calls: number;
  bookings: number;
  food_orders: number;
  conversations: number;
}

export interface GBPDailyInsight {
  date: string;
  total_views: number;
  search_views: number;
  maps_views: number;
  desktop_views: number;
  mobile_views: number;
  website_clicks: number;
  direction_requests: number;
  phone_calls: number;
}

export interface GBPDashboard {
  accounts: {
    total: number;
  };
  locations: {
    total: number;
    total_reviews: number;
    average_rating: number;
    total_views: number;
    total_website_clicks: number;
    total_direction_requests: number;
    total_phone_calls: number;
  };
  reviews: {
    total: number;
    needs_reply: number;
  };
  posts: {
    total: number;
  };
  photos: {
    total: number;
  };
  recent_reviews: GBPReview[];
  accounts_list: GBPAccount[];
  top_locations: GBPLocation[];
}

// Google Search Console Types
export interface GSCProperty {
  id: string;
  client: string;
  client_name: string | null;
  site_url: string;
  display_name: string;
  permission_level: 'siteOwner' | 'siteFullUser' | 'siteRestrictedUser' | 'siteUnverifiedUser' | '';
  is_active: boolean;
  last_synced_at: string | null;
  total_clicks: number;
  total_impressions: number;
  average_ctr: string;
  average_position: string;
  queries_count: number;
  pages_count: number;
  sitemaps_count: number;
  created_at: string;
  updated_at: string;
}

export interface GSCSitemap {
  id: string;
  property: string;
  property_name: string | null;
  path: string;
  sitemap_type: string;
  last_submitted: string | null;
  last_downloaded: string | null;
  is_pending: boolean;
  is_sitemaps_index: boolean;
  warnings: number;
  errors: number;
  created_at: string;
  updated_at: string;
}

export interface GSCQuery {
  id: string;
  property: string;
  property_name: string | null;
  query: string;
  date_start: string;
  date_end: string;
  clicks: number;
  impressions: number;
  ctr: string;
  position: string;
  created_at: string;
  updated_at: string;
}

export interface GSCPage {
  id: string;
  property: string;
  property_name: string | null;
  page_url: string;
  date_start: string;
  date_end: string;
  clicks: number;
  impressions: number;
  ctr: string;
  position: string;
  created_at: string;
  updated_at: string;
}

export interface GSCSnapshot {
  id: string;
  property: string;
  date: string;
  clicks: number;
  impressions: number;
  ctr: string;
  position: string;
  created_at: string;
}

export interface GSCDashboard {
  total_properties: number;
  stats: {
    total_clicks: number;
    total_impressions: number;
    average_ctr: number;
    average_position: number;
  };
  properties: GSCProperty[];
}

export interface ClientMarketingSummary {
  client: Client;
  google_ads: {
    accounts_count: number;
    total_spend: number;
    total_clicks: number;
    total_impressions: number;
    total_conversions: number;
  };
  gbp: {
    accounts_count: number;
    locations_count: number;
    total_reviews: number;
    avg_rating: number;
    total_views: number;
  };
  gsc: {
    properties_count: number;
    total_clicks: number;
    total_impressions: number;
    avg_ctr: number;
    avg_position: number;
  };
}

export interface GSCAnalytics {
  summary: {
    period_start: string;
    period_end: string;
    total_clicks: number;
    total_impressions: number;
    average_ctr: number;
    average_position: number;
  };
  daily_data: Array<{
    date: string;
    clicks: number;
    impressions: number;
    ctr: number;
    position: number;
  }>;
  top_queries: GSCQuery[];
  top_pages: GSCPage[];
}

export interface Candidate {
  id: string;
  name: string;
  email: string;
  phone: string;
  role: string;
  department: string;
  status: 'new' | 'screening' | 'interview' | 'offer' | 'hired' | 'rejected';
  experience: string;
  skills: string[];
  resume_url: string;
  notes: string;
  applied_date: string;
  salary_expectation: string | null;
  created_at: string;
  updated_at: string;
}

export interface SupportRequest {
  id: string;
  title: string;
  request_type: 'support' | 'feature' | 'bug' | 'question';
  priority: 'low' | 'medium' | 'high' | 'urgent';
  status: 'new' | 'in_progress' | 'resolved' | 'closed';
  description: string;
  client: string;
  client_name: string | null;
  assigned_to: string | null;
  assigned_to_name: string | null;
  comments_count: number;
  created_at: string;
  updated_at: string;
}

export interface PaginatedResponse<T> {
  count: number;
  next: string | null;
  previous: string | null;
  results: T[];
}


// Email interfaces
export interface EmailAccount {
  id: string;
  email_address: string;
  display_name: string;
  imap_host: string;
  imap_port: number;
  smtp_host: string;
  smtp_port: number;
  is_active: boolean;
  last_synced: string | null;
  unread_count: number;
  created_at: string;
  updated_at: string;
}

export interface EmailFolder {
  name: string;
  unread: number;
  total: number;
}

export interface EmailMessageSummary {
  id: string;
  message_uid: string;
  message_id: string;
  folder: string;
  subject: string;
  from_address: string;
  to_addresses: string[];
  cc_addresses: string[];
  date: string;
  snippet: string;
  is_read: boolean;
  is_starred: boolean;
  has_attachments: boolean;
  in_reply_to: string;
  references: string;
  created_at: string;
}

export interface EmailMessageDetail extends EmailMessageSummary {
  html_body: string;
  plain_body: string;
}

// ==========================================================================
// SEO Stack Interfaces
// ==========================================================================

export interface CrawlConfig {
  id: string;
  client: string;
  root_url: string;
  max_pages: number;
  crawl_depth: number;
  rate_limit_rps: number;
  respect_robots_txt: boolean;
  user_agent: string;
  exclude_patterns: string[];
  include_patterns: string[];
  is_active: boolean;
  schedule_frequency: string;
  last_crawl_at: string | null;
  created_at: string;
}

export interface CrawlRun {
  id: string;
  client: string;
  client_name?: string;
  status: 'pending' | 'running' | 'completed' | 'failed' | 'cancelled';
  started_at: string | null;
  completed_at: string | null;
  pages_discovered: number;
  pages_crawled: number;
  pages_errored: number;
  duration_seconds: number;
  trigger: string;
  error_message: string;
}

export interface CrawledPage {
  id: string;
  crawl_run: string;
  url: string;
  final_url: string;
  status_code: number;
  content_type: string;
  response_time_ms: number;
  is_indexable: boolean;
  noindex: boolean;
  nofollow: boolean;
  canonical_url: string;
  canonical_is_self: boolean;
  title: string;
  meta_description: string;
  title_length: number;
  meta_description_length: number;
  h1: string[];
  h2: string[];
  h1_count: number;
  word_count: number;
  body_text_hash: string;
  internal_links_out: any[];
  external_links_out: any[];
  internal_links_in_count: number;
  images: any[];
  images_without_alt: number;
  schema_types: string[];
  has_schema: boolean;
  page_type: string;
  crawl_depth: number;
}

export interface SEOFinding {
  id: string;
  client: string;
  client_name?: string;
  crawl_run: string | null;
  engine: string;
  rule_id: string;
  severity: 'info' | 'low' | 'medium' | 'high' | 'critical';
  page_url: string;
  observation: string;
  recommendation: string;
  impact_score: number;
  effort_score: number;
  priority_score: number;
  is_resolved: boolean;
  resolved_at: string | null;
  is_suppressed: boolean;
  confidence: string;
  metrics: Record<string, any>;
  created_at: string;
}

export interface SEOChangeEvent {
  id: string;
  client: string;
  page_url: string;
  change_type: string;
  severity: string;
  old_value: string;
  new_value: string;
  is_acknowledged: boolean;
  created_at: string;
}

export interface LinkEquityScore {
  id: string;
  crawl_run: string;
  page_url: string;
  inlinks_count: number;
  outlinks_count: number;
  internal_pagerank: number;
  is_orphan: boolean;
  hub_score: number;
}

export interface KeywordPageMapping {
  id: string;
  keyword: string;
  city_modifier: string;
  full_query: string;
  target_page_url: string;
  gsc_clicks: number;
  gsc_impressions: number;
  gsc_ctr: number;
  gsc_position: number;
  is_primary: boolean;
  is_cannibalized: boolean;
  cannibalized_by_urls: string[];
}

export interface CoverageGap {
  id: string;
  service_keyword: string;
  city: string;
  gap_type: 'no_page' | 'no_ranking' | 'thin_content';
  existing_page_url: string;
  estimated_search_volume: number;
  market_city?: string;
  market_region?: string;
}

export interface SEODashboard {
  findings_by_severity: Record<string, number>;
  findings_by_engine: Record<string, number>;
  crawl_status: {
    latest_crawl: CrawlRun | null;
    total_runs: number;
    pages_crawled: number;
  };
  coverage_gap_count: number;
  top_priorities: SEOFinding[];
}

export interface ClientSEOSummary {
  latest_crawl: CrawlRun | null;
  audit_score: number;
  finding_counts: {
    critical: number;
    high: number;
    medium: number;
    low: number;
    info: number;
    total: number;
  };
  coverage_gap_count: number;
}

// ===========================================================================
// PROSPECT/OUTREACH TYPES
// ===========================================================================

export interface ProspectList {
  id: string;
  name: string;
  description: string;
  source: string;
  prospect_count: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface ProspectListDetail extends ProspectList {
  imports: ProspectImport[];
}

export interface Prospect {
  id: string;
  prospect_list: string;
  prospect_list_name: string;
  name: string;
  company_name: string;
  category: string;
  city: string;
  state: string;
  contact_person: string;
  phone: string;
  email: string;
  website: string;
  status: 'new' | 'enriching' | 'scored' | 'queued' | 'contacting' | 'nurture' | 'won' | 'lost';
  assigned_to: string | null;
  assigned_to_name: string | null;
  priority: 'low' | 'medium' | 'high';
  last_contacted_at: string | null;
  next_follow_up: string | null;
  next_action_at: string | null;
  notes: string;
  converted_to_deal: string | null;
  created_at: string;
  // Enrichment fields
  vertical: string;
  source: string;
  tier: number;
  fit_score: number;
  confidence: 'high' | 'medium' | 'low';
  touch_count: number;
  // Marketing Index
  seo_rank: number | null;
  marketing_score: number;
  // Owner info
  owner_name: string;
  owner_email: string;
  owner_visible: boolean | null;
  decision_maker_role: string;
  // Google Maps data (from Scrap.io)
  review_rating: number | null;
  review_count: number;
  google_business_url: string;
}

export interface ProspectDetail extends Prospect {
  street_address: string;
  zip_code: string;
  address_full: string;
  description: string;
  member_since: string;
  social_media: Record<string, string>;
  detail_url: string;
  tags: string[];
  notes: string;
  deal_id: string | null;
  activities: ProspectActivity[];
  touches: ProspectTouch[];
  updated_at: string;
  // Signal fields
  has_financing: boolean;
  financing_provider: string;
  has_marketing_tools: boolean;
  tools_detected: string[];
  review_count: number;
  review_rating: number | null;
  review_velocity_90d: number;
  google_business_url: string;
  has_branded_fleet: boolean;
  fleet_estimate: number | null;
  social_post_freq: string;
  certifications: string[];
  runs_google_ads: boolean;
  ads_evidence: string;
  website_quality: string;
  has_agency_partner: boolean | null;
  is_enterprise: boolean;
  sells_high_ticket: boolean | null;
  hiring_signals: boolean | null;
  has_growth_signals: boolean;
  service_area: string[];
  // Scoring
  score_reason: string;
  scored_at: string | null;
  enriched_at: string | null;
  // Human verification
  human_verified: boolean;
  human_verified_at: string | null;
}

export interface ProspectTouch {
  id: string;
  prospect: string;
  user: string | null;
  user_name: string | null;
  channel: 'call' | 'email' | 'sms' | 'linkedin' | 'mail';
  template_id: string;
  sent_at: string;
  result: 'pending' | 'no_answer' | 'left_vm' | 'replied' | 'booked' | 'not_interested' | 'bad_data';
  reply_text: string;
  notes: string;
  next_step: string;
  next_action_at: string | null;
}

export interface ProspectActivity {
  id: string;
  prospect: string;
  user: string | null;
  user_name: string | null;
  activity_type: 'note' | 'call' | 'email' | 'linkedin' | 'meeting' | 'research' | 'status_change';
  description: string;
  metadata: Record<string, unknown>;
  created_at: string;
}

export interface ProspectImport {
  id: string;
  prospect_list: string;
  prospect_list_name: string;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  file_name: string;
  total_rows: number;
  processed_rows: number;
  created_count: number;
  skipped_count: number;
  error_count: number;
  errors: Array<{ row: number; error: string }>;
  started_at: string | null;
  completed_at: string | null;
  created_at: string;
}

export interface ProspectStats {
  total: number;
  by_status: Array<{ status: string; count: number }>;
  by_vertical: Array<{ vertical: string; count: number }>;
  by_tier: Array<{ tier: number; count: number }>;
  by_source: Array<{ source: string; count: number }>;
  by_category: Array<{ category: string; count: number }>;
  converted: number;
  conversion_rate: number;
  overdue_followups: number;
  today_followups: number;
  overdue_actions: number;
  avg_score: number;
  score_distribution: {
    high: number;
    medium: number;
    low: number;
  };
  total_touches: number;
  recent_touches: number;
}

export interface EnrichmentJob {
  id: string;
  prospect: string;
  prospect_name: string;
  user: string | null;
  status: 'pending' | 'searching' | 'extracting' | 'review' | 'approved' | 'rejected' | 'failed';
  search_queries: string[];
  search_results: Record<string, Array<{ title: string; url: string; description: string }>>;
  extracted_data: EnrichmentExtractedData;
  reviewed_data: EnrichmentExtractedData;
  reviewed_by: string | null;
  reviewed_at: string | null;
  celery_task_id: string;
  error_message: string;
  created_at: string;
  started_at: string | null;
  completed_at: string | null;
}

export interface EnrichmentExtractedData {
  // Company Info
  google_business_url?: string | null;
  website?: string | null;
  owner_name?: string | null;
  owner_visible?: boolean | null;

  // Reviews
  review_rating?: number | null;
  review_count?: number | null;
  review_velocity_90d?: number | null;

  // Marketing Signals
  runs_google_ads?: boolean | null;
  ads_evidence?: string | null;
  has_marketing_tools?: boolean | null;
  tools_detected?: string[];
  social_post_freq?: 'none' | 'monthly' | 'weekly' | 'daily';

  // Business Signals
  has_branded_fleet?: boolean | null;
  fleet_estimate?: number | null;
  sells_high_ticket?: boolean | null;
  has_financing?: boolean | null;
  financing_provider?: string | null;
  hiring_signals?: boolean | null;
  is_enterprise?: boolean | null;
  certifications?: string[];

  // Contact Info
  owner_email?: string | null;
  phone?: string | null;

  // Qualification
  confidence?: 'high' | 'medium' | 'low';
  score_reason?: string | null;

  // Error handling
  error?: string;
  raw_response?: string;
}

class ApiService {
  // Mutex for token refresh - prevents race conditions when multiple requests get 401
  private refreshPromise: Promise<boolean> | null = null;
  private isRedirecting = false;

  private getToken(): string | null {
    return localStorage.getItem('access_token');
  }

  private getRefreshToken(): string | null {
    return localStorage.getItem('refresh_token');
  }

  private setTokens(access: string, refresh: string): void {
    localStorage.setItem('access_token', access);
    localStorage.setItem('refresh_token', refresh);
  }

  private clearTokens(): void {
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('user');
  }

  private redirectToLogin(): never {
    // Prevent multiple redirects
    if (this.isRedirecting) {
      // Return a never-resolving promise to halt execution
      return new Promise(() => {}) as never;
    }
    this.isRedirecting = true;
    this.clearTokens();
    window.location.href = '/login';
    // Return a never-resolving promise to halt execution
    return new Promise(() => {}) as never;
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    // If we're already redirecting, don't make any more requests
    if (this.isRedirecting) {
      return new Promise(() => {}) as T;
    }

    const token = this.getToken();

    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      ...options.headers,
    };

    if (token) {
      (headers as Record<string, string>)['Authorization'] = `Bearer ${token}`;
    }

    const response = await fetch(`${API_URL}${endpoint}`, {
      ...options,
      headers,
    });

    if (response.status === 401) {
      // Parse the error response to determine if it's a JWT auth failure or a business logic 401
      const errorBody = await response.json().catch(() => ({}));

      // If the response has "error" field (not "detail"), it's a business logic 401
      // (e.g., Google OAuth token expired, third-party API auth failure)
      // Don't logout - just throw the error so it can be displayed to the user
      if (errorBody.error && !errorBody.detail) {
        throw new Error(errorBody.error);
      }

      // It's likely a JWT auth failure - try to refresh token
      const refreshed = await this.refreshTokenWithMutex();
      if (refreshed) {
        // Retry the request with new token
        const newToken = this.getToken();
        (headers as Record<string, string>)['Authorization'] = `Bearer ${newToken}`;
        const retryResponse = await fetch(`${API_URL}${endpoint}`, {
          ...options,
          headers,
        });
        if (retryResponse.status === 401) {
          // Check again if it's a business logic 401
          const retryErrorBody = await retryResponse.json().catch(() => ({}));
          if (retryErrorBody.error && !retryErrorBody.detail) {
            throw new Error(retryErrorBody.error);
          }
          // Token refresh didn't help, redirect to login
          return this.redirectToLogin();
        }
        if (!retryResponse.ok) {
          const error = await retryResponse.json().catch(() => ({}));
          throw new Error(error.detail || error.error || error.message || `API Error: ${retryResponse.status}`);
        }
        return retryResponse.json();
      } else {
        // Refresh failed, redirect to login
        return this.redirectToLogin();
      }
    }

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.detail || error.error || error.message || `API Error: ${response.status}`);
    }

    // Handle 204 No Content (e.g., DELETE responses)
    if (response.status === 204 || response.headers.get('content-length') === '0') {
      return {} as T;
    }

    return response.json();
  }

  // Token refresh with mutex - ensures only one refresh happens at a time
  private async refreshTokenWithMutex(): Promise<boolean> {
    // If a refresh is already in progress, wait for it
    if (this.refreshPromise) {
      return this.refreshPromise;
    }

    // Start a new refresh and store the promise
    this.refreshPromise = this.doRefreshToken();

    try {
      const result = await this.refreshPromise;
      return result;
    } finally {
      // Clear the promise so future refreshes can happen
      this.refreshPromise = null;
    }
  }

  private async doRefreshToken(): Promise<boolean> {
    const refreshToken = this.getRefreshToken();
    if (!refreshToken) return false;

    try {
      const response = await fetch(`${API_URL}/auth/refresh/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refresh: refreshToken }),
      });

      if (response.ok) {
        const data = await response.json();
        this.setTokens(data.access, data.refresh || refreshToken);
        return true;
      }
      return false;
    } catch {
      return false;
    }
  }

  // Auth methods
  async login(username: string, password: string): Promise<TokenResponse> {
    const response = await fetch(`${API_URL}/auth/login/`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ username, password }),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.detail || 'Invalid credentials');
    }

    const data: TokenResponse = await response.json();
    this.setTokens(data.access, data.refresh);
    localStorage.setItem('user', JSON.stringify(data.user));
    return data;
  }

  async register(userData: {
    username: string;
    email: string;
    password: string;
    password_confirm: string;
    first_name: string;
    last_name: string;
  }): Promise<{ message: string; user_id: string }> {
    const response = await fetch(`${API_URL}/auth/register/`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(userData),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.detail || Object.values(error).flat().join(', ') || 'Registration failed');
    }

    return response.json();
  }

  // Password reset methods
  async requestPasswordReset(email: string): Promise<{ message: string }> {
    const response = await fetch(`${API_URL}/auth/password-reset/`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email }),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.detail || error.error || 'Password reset request failed');
    }

    return response.json();
  }

  async confirmPasswordReset(uid: string, token: string, password: string, password_confirm: string): Promise<{ message: string }> {
    const response = await fetch(`${API_URL}/auth/password-reset/confirm/`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ uid, token, password, password_confirm }),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.detail || error.error || 'Password reset failed');
    }

    return response.json();
  }

  logout(): void {
    this.clearTokens();
    window.location.href = '/login';
  }

  isAuthenticated(): boolean {
    return !!this.getToken();
  }

  getUser(): User | null {
    const userStr = localStorage.getItem('user');
    return userStr ? JSON.parse(userStr) : null;
  }

  // Dashboard
  async getDashboard(): Promise<{ user: User; stats: Record<string, unknown> }> {
    return this.request('/dashboard/');
  }

  async getDashboardStats(): Promise<DashboardStats> {
    return this.request('/dashboard/stats/');
  }

  // Clients
  async getClients(params?: {
    status?: string;
    search?: string;
    ordering?: string;
    page?: number;
  }): Promise<PaginatedResponse<Client>> {
    const searchParams = new URLSearchParams();
    if (params?.status && params.status !== 'all') searchParams.append('status', params.status);
    if (params?.search) searchParams.append('search', params.search);
    if (params?.ordering) searchParams.append('ordering', params.ordering);
    if (params?.page) searchParams.append('page', params.page.toString());

    const query = searchParams.toString();
    return this.request(`/clients/${query ? `?${query}` : ''}`);
  }

  async getClient(id: string): Promise<Client> {
    return this.request(`/clients/${id}/`);
  }

  async createClient(data: Partial<Client>): Promise<Client> {
    return this.request('/clients/', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateClient(id: string, data: Partial<Client>): Promise<Client> {
    return this.request(`/clients/${id}/`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async deleteClient(id: string): Promise<void> {
    await this.request(`/clients/${id}/`, { method: 'DELETE' });
  }

  async transitionClientPhase(id: string): Promise<Client> {
    return this.request(`/clients/${id}/transition-phase/`, { method: "POST" });
  }

  async getClientMarketingSummary(id: string): Promise<ClientMarketingSummary> {
    return this.request(`/clients/${id}/marketing-summary/`);
  }

  // Client Markets
  async getClientMarkets(clientId: string): Promise<Market[]> {
    return this.request(`/clients/${clientId}/markets/`);
  }

  async createMarket(clientId: string, data: Partial<Market>): Promise<Market> {
    return this.request(`/clients/${clientId}/markets/`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateMarket(clientId: string, marketId: string, data: Partial<Market>): Promise<Market> {
    return this.request(`/clients/${clientId}/markets/${marketId}/`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async deleteMarket(clientId: string, marketId: string): Promise<void> {
    await this.request(`/clients/${clientId}/markets/${marketId}/`, { method: 'DELETE' });
  }

  // Client Services
  async getClientServices(clientId: string): Promise<ClientService[]> {
    const response = await this.request<{ results: ClientService[] } | ClientService[]>(`/clients/${clientId}/services/`);
    // Handle both paginated and non-paginated responses
    return Array.isArray(response) ? response : (response.results || []);
  }

  async createClientService(clientId: string, data: { service: string; is_active?: boolean }): Promise<ClientService> {
    return this.request(`/clients/${clientId}/services/`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateClientService(clientId: string, serviceId: string, data: Partial<ClientService>): Promise<ClientService> {
    return this.request(`/clients/${clientId}/services/${serviceId}/`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async deleteClientService(clientId: string, serviceId: string): Promise<void> {
    await this.request(`/clients/${clientId}/services/${serviceId}/`, { method: 'DELETE' });
  }

  // Onboarding Test Mode
  async getTestModeStatus(clientId: string): Promise<{ test_mode: boolean; test_phone: string }> {
    return this.request(`/clients/${clientId}/onboarding/test-mode/status/`);
  }

  async setTestMode(clientId: string, enabled: boolean, testPhone?: string): Promise<{ test_mode: boolean; message: string }> {
    return this.request(`/clients/${clientId}/onboarding/test-mode/`, {
      method: 'POST',
      body: JSON.stringify({ enabled, test_phone: testPhone }),
    });
  }

  // Classification checks
  async runClientChecks(clientId: string): Promise<{
    findings: ClientFinding[];
    sop_packs: string[];
    gbp_count: number;
    markets_count: number;
  }> {
    return this.request(`/clients/${clientId}/run-checks/`, { method: 'POST' });
  }

  // Projects
  async getProjects(params?: {
    status?: string;
    project_type?: string;
    client?: string;
    search?: string;
    page?: number;
  }): Promise<PaginatedResponse<Project>> {
    const searchParams = new URLSearchParams();
    if (params?.status && params.status !== 'all') searchParams.append('status', params.status);
    if (params?.project_type && params.project_type !== 'all') searchParams.append('project_type', params.project_type);
    if (params?.search) searchParams.append('search', params.search);
    if (params?.page) searchParams.append('page', params.page.toString());

    const query = searchParams.toString();
    return this.request(`/projects/${query ? `?${query}` : ''}`);
  }

  async getProject(id: string): Promise<Project> {
    return this.request(`/projects/${id}/`);
  }

  async createProject(data: Partial<Project>): Promise<Project> {
    return this.request('/projects/', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateProject(id: string, data: Partial<Project>): Promise<Project> {
    return this.request(`/projects/${id}/`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteProject(id: string): Promise<void> {
    await this.request(`/projects/${id}/`, { method: 'DELETE' });
  }

  // Services
  async getServices(params?: {
    service_type?: string;
    is_active?: boolean;
    search?: string;
  }): Promise<PaginatedResponse<Service>> {
    const searchParams = new URLSearchParams();
    if (params?.service_type) searchParams.append('service_type', params.service_type);
    if (params?.is_active !== undefined) searchParams.append('is_active', params.is_active.toString());
    if (params?.search) searchParams.append('search', params.search);

    const query = searchParams.toString();
    return this.request(`/services/${query ? `?${query}` : ''}`);
  }

  // Campaigns
  async getCampaigns(params?: {
    status?: string;
    campaign_type?: string;
    client?: string;
    search?: string;
    page?: number;
  }): Promise<PaginatedResponse<Campaign>> {
    const searchParams = new URLSearchParams();
    if (params?.status && params.status !== 'all') searchParams.append('status', params.status);
    if (params?.campaign_type && params.campaign_type !== 'all') searchParams.append('campaign_type', params.campaign_type);
    if (params?.search) searchParams.append('search', params.search);
    if (params?.page) searchParams.append('page', params.page.toString());

    const query = searchParams.toString();
    return this.request(`/campaigns/${query ? `?${query}` : ''}`);
  }

  async getCampaign(id: string): Promise<Campaign> {
    return this.request(`/campaigns/${id}/`);
  }

  async createCampaign(data: Partial<Campaign>): Promise<Campaign> {
    return this.request('/campaigns/', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateCampaign(id: string, data: Partial<Campaign>): Promise<Campaign> {
    return this.request(`/campaigns/${id}/`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteCampaign(id: string): Promise<void> {
    await this.request(`/campaigns/${id}/`, { method: 'DELETE' });
  }

  // Industry Verticals
  async getIndustries(): Promise<PaginatedResponse<{ id: string; name: string; description: string }>> {
    return this.request('/verticals/');
  }

  // Team Members
  async getTeamMembers(params?: {
    role?: string;
    is_active_agent?: boolean;
    search?: string;
    page?: number;
  }): Promise<PaginatedResponse<TeamMember>> {
    const searchParams = new URLSearchParams();
    if (params?.role && params.role !== 'all') searchParams.append('role', params.role);
    if (params?.is_active_agent !== undefined) searchParams.append('is_active_agent', params.is_active_agent.toString());
    if (params?.search) searchParams.append('search', params.search);
    if (params?.page) searchParams.append('page', params.page.toString());

    const query = searchParams.toString();
    return this.request(`/team/${query ? `?${query}` : ''}`);
  }

  async getTeamMember(id: string): Promise<TeamMember> {
    return this.request(`/team/${id}/`);
  }

  async updateTeamMember(id: string, data: Partial<TeamMember>): Promise<TeamMember> {
    return this.request(`/team/${id}/`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  // Transactions (Finance)
  async getTransactions(params?: {
    transaction_type?: string;
    status?: string;
    category?: string;
    search?: string;
    page?: number;
  }): Promise<PaginatedResponse<Transaction>> {
    const searchParams = new URLSearchParams();
    if (params?.transaction_type && params.transaction_type !== 'all') searchParams.append('transaction_type', params.transaction_type);
    if (params?.status && params.status !== 'all') searchParams.append('status', params.status);
    if (params?.category && params.category !== 'all') searchParams.append('category', params.category);
    if (params?.search) searchParams.append('search', params.search);
    if (params?.page) searchParams.append('page', params.page.toString());

    const query = searchParams.toString();
    return this.request(`/transactions/${query ? `?${query}` : ''}`);
  }

  async getTransaction(id: string): Promise<Transaction> {
    return this.request(`/transactions/${id}/`);
  }

  async createTransaction(data: Partial<Transaction>): Promise<Transaction> {
    return this.request('/transactions/', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateTransaction(id: string, data: Partial<Transaction>): Promise<Transaction> {
    return this.request(`/transactions/${id}/`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteTransaction(id: string): Promise<void> {
    await this.request(`/transactions/${id}/`, { method: 'DELETE' });
  }

  async getFinanceStats(days?: number): Promise<FinanceStats> {
    const params = days ? `?days=${days}` : '';
    return this.request(`/finance/stats/${params}`);
  }

  // Mercury Banking API
  async getMercuryAccounts(): Promise<{ accounts: MercuryAccount[]; count: number }> {
    return this.request('/mercury/accounts/');
  }

  async getMercuryTransactions(params?: {
    start_date?: string;
    end_date?: string;
    status?: string;
  }): Promise<{ results: MercuryTransaction[]; count: number }> {
    const searchParams = new URLSearchParams();
    if (params?.start_date) searchParams.append('start_date', params.start_date);
    if (params?.end_date) searchParams.append('end_date', params.end_date);
    if (params?.status) searchParams.append('status', params.status);

    const query = searchParams.toString();
    return this.request(`/mercury/transactions/${query ? `?${query}` : ''}`);
  }

  // Google Ads API
  async getGoogleAdsAuthUrl(): Promise<{ auth_url: string; state: string }> {
    return this.request('/google-ads/auth/');
  }

  async googleAdsOAuthCallback(code: string, state?: string): Promise<GoogleAdsOAuthResponse> {
    return this.request('/google-ads/callback/', {
      method: 'POST',
      body: JSON.stringify({ code, state }),
    });
  }

  async linkGoogleAdsAccount(data: {
    client_id: string;
    customer_id: string;
    name: string;
    access_token: string;
    refresh_token: string;
    expires_in: number;
    currency_code?: string;
    timezone?: string;
    login_customer_id?: string;  // Required when linking a sub-account of a Manager/MCC account
  }): Promise<GoogleAdsAccount> {
    return this.request('/google-ads/link/', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async getGoogleAdsDashboard(clientId?: string): Promise<GoogleAdsDashboard> {
    const params = clientId ? `?client=${clientId}` : '';
    return this.request(`/google-ads/dashboard/${params}`);
  }

  async getGoogleAdsAccounts(params?: {
    client?: string;
    is_active?: boolean;
    search?: string;
  }): Promise<PaginatedResponse<GoogleAdsAccount>> {
    const searchParams = new URLSearchParams();
    if (params?.is_active !== undefined) searchParams.append('is_active', params.is_active.toString());
    if (params?.search) searchParams.append('search', params.search);

    const query = searchParams.toString();
    return this.request(`/google-ads/accounts/${query ? `?${query}` : ''}`);
  }

  async getGoogleAdsAccount(id: string): Promise<GoogleAdsAccount> {
    return this.request(`/google-ads/accounts/${id}/`);
  }

  async syncGoogleAdsAccount(id: string): Promise<{
    message: string;
    summary: GoogleAdsAccountSummary;
    campaigns_synced: number;
  }> {
    return this.request(`/google-ads/accounts/${id}/sync/`, { method: 'POST' });
  }

  async getGoogleAdsAccountAnalytics(id: string, days?: number): Promise<{
    summary: GoogleAdsAccountSummary;
    daily_data: GoogleAdsDailyData[];
  }> {
    const params = days ? `?days=${days}` : '';
    return this.request(`/google-ads/accounts/${id}/analytics/${params}`);
  }

  async getGoogleAdsCampaigns(params?: {
    account?: string;
    status?: string;
    campaign_type?: string;
    search?: string;
    ordering?: string;
    page?: number;
  }): Promise<PaginatedResponse<GoogleAdsCampaign>> {
    const searchParams = new URLSearchParams();
    if (params?.account) searchParams.append('account', params.account);
    if (params?.status && params.status !== 'all') searchParams.append('status', params.status);
    if (params?.campaign_type && params.campaign_type !== 'all') searchParams.append('campaign_type', params.campaign_type);
    if (params?.search) searchParams.append('search', params.search);
    if (params?.ordering) searchParams.append('ordering', params.ordering);
    if (params?.page) searchParams.append('page', params.page.toString());

    const query = searchParams.toString();
    return this.request(`/google-ads/campaigns/${query ? `?${query}` : ''}`);
  }

  async getGoogleAdsCampaign(id: string): Promise<GoogleAdsCampaign> {
    return this.request(`/google-ads/campaigns/${id}/`);
  }

  async getGoogleAdsAdGroups(params?: {
    campaign?: string;
    status?: string;
    search?: string;
  }): Promise<PaginatedResponse<GoogleAdsAdGroup>> {
    const searchParams = new URLSearchParams();
    if (params?.campaign) searchParams.append('campaign', params.campaign);
    if (params?.status && params.status !== 'all') searchParams.append('status', params.status);
    if (params?.search) searchParams.append('search', params.search);

    const query = searchParams.toString();
    return this.request(`/google-ads/ad-groups/${query ? `?${query}` : ''}`);
  }

  async getGoogleAdsAds(params?: {
    ad_group?: string;
    status?: string;
    approval_status?: string;
    ad_type?: string;
    search?: string;
  }): Promise<PaginatedResponse<GoogleAdsAd>> {
    const searchParams = new URLSearchParams();
    if (params?.ad_group) searchParams.append('ad_group', params.ad_group);
    if (params?.status && params.status !== 'all') searchParams.append('status', params.status);
    if (params?.approval_status && params.approval_status !== 'all') searchParams.append('approval_status', params.approval_status);
    if (params?.ad_type && params.ad_type !== 'all') searchParams.append('ad_type', params.ad_type);
    if (params?.search) searchParams.append('search', params.search);

    const query = searchParams.toString();
    return this.request(`/google-ads/ads/${query ? `?${query}` : ''}`);
  }

  async getGoogleAdsKeywords(params?: {
    ad_group?: string;
    status?: string;
    match_type?: string;
    search?: string;
    ordering?: string;
  }): Promise<PaginatedResponse<GoogleAdsKeyword>> {
    const searchParams = new URLSearchParams();
    if (params?.ad_group) searchParams.append('ad_group', params.ad_group);
    if (params?.status && params.status !== 'all') searchParams.append('status', params.status);
    if (params?.match_type && params.match_type !== 'all') searchParams.append('match_type', params.match_type);
    if (params?.search) searchParams.append('search', params.search);
    if (params?.ordering) searchParams.append('ordering', params.ordering);

    const query = searchParams.toString();
    return this.request(`/google-ads/keywords/${query ? `?${query}` : ''}`);
  }

  // Google Ads Advanced Analytics Methods
  async getGoogleAdsSearchTerms(accountId: string, params?: {
    days?: number;
    campaign_id?: string;
  }): Promise<{ search_terms: GoogleAdsSearchTerm[]; period_start: string; period_end: string }> {
    const searchParams = new URLSearchParams();
    if (params?.days) searchParams.append('days', params.days.toString());
    if (params?.campaign_id) searchParams.append('campaign_id', params.campaign_id);
    const query = searchParams.toString();
    return this.request(`/google-ads/accounts/${accountId}/search-terms/${query ? `?${query}` : ''}`);
  }

  async getGoogleAdsConversionActions(accountId: string): Promise<{ conversion_actions: GoogleAdsConversionAction[] }> {
    return this.request(`/google-ads/accounts/${accountId}/conversion-actions/`);
  }

  async getGoogleAdsConversionMetrics(accountId: string, params?: {
    days?: number;
    primary_only?: boolean;
  }): Promise<{ metrics: GoogleAdsConversionMetric[]; period_start: string; period_end: string }> {
    const searchParams = new URLSearchParams();
    if (params?.days) searchParams.append('days', params.days.toString());
    if (params?.primary_only) searchParams.append('primary_only', 'true');
    const query = searchParams.toString();
    return this.request(`/google-ads/accounts/${accountId}/conversion-metrics/${query ? `?${query}` : ''}`);
  }

  async getGoogleAdsPerformanceByDevice(accountId: string, params?: {
    days?: number;
  }): Promise<{ data: GoogleAdsDevicePerformance[]; period_start: string; period_end: string }> {
    const searchParams = new URLSearchParams();
    if (params?.days) searchParams.append('days', params.days.toString());
    const query = searchParams.toString();
    return this.request(`/google-ads/accounts/${accountId}/performance-by-device/${query ? `?${query}` : ''}`);
  }

  async getGoogleAdsPerformanceByGeo(accountId: string, params?: {
    days?: number;
  }): Promise<{ data: GoogleAdsGeoPerformance[]; period_start: string; period_end: string }> {
    const searchParams = new URLSearchParams();
    if (params?.days) searchParams.append('days', params.days.toString());
    const query = searchParams.toString();
    return this.request(`/google-ads/accounts/${accountId}/performance-by-geo/${query ? `?${query}` : ''}`);
  }

  async getGoogleAdsPerformanceByDayOfWeek(accountId: string, params?: {
    days?: number;
  }): Promise<{ data: GoogleAdsDayOfWeekPerformance[]; period_start: string; period_end: string }> {
    const searchParams = new URLSearchParams();
    if (params?.days) searchParams.append('days', params.days.toString());
    const query = searchParams.toString();
    return this.request(`/google-ads/accounts/${accountId}/performance-by-day-of-week/${query ? `?${query}` : ''}`);
  }

  async getGoogleAdsPerformanceByHour(accountId: string, params?: {
    days?: number;
  }): Promise<{ data: GoogleAdsHourPerformance[]; period_start: string; period_end: string }> {
    const searchParams = new URLSearchParams();
    if (params?.days) searchParams.append('days', params.days.toString());
    const query = searchParams.toString();
    return this.request(`/google-ads/accounts/${accountId}/performance-by-hour/${query ? `?${query}` : ''}`);
  }

  async getGoogleAdsImpressionShare(accountId: string, params?: {
    days?: number;
  }): Promise<{ data: GoogleAdsImpressionShare[]; period_start: string; period_end: string }> {
    const searchParams = new URLSearchParams();
    if (params?.days) searchParams.append('days', params.days.toString());
    const query = searchParams.toString();
    return this.request(`/google-ads/accounts/${accountId}/impression-share/${query ? `?${query}` : ''}`);
  }

  async getGoogleAdsAuctionInsights(accountId: string, params?: {
    days?: number;
    campaign_id?: string;
  }): Promise<{ insights: GoogleAdsAuctionInsight[]; period_start: string; period_end: string }> {
    const searchParams = new URLSearchParams();
    if (params?.days) searchParams.append('days', params.days.toString());
    if (params?.campaign_id) searchParams.append('campaign_id', params.campaign_id);
    const query = searchParams.toString();
    return this.request(`/google-ads/accounts/${accountId}/auction-insights/${query ? `?${query}` : ''}`);
  }

  async getGoogleAdsOptimizationAudit(accountId: string, params?: {
    days?: number;
  }): Promise<GoogleAdsOptimizationAudit> {
    const searchParams = new URLSearchParams();
    if (params?.days) searchParams.append('days', params.days.toString());
    const query = searchParams.toString();
    return this.request(`/google-ads/accounts/${accountId}/optimization-audit/${query ? `?${query}` : ''}`);
  }

  // Data integrity debug for optimization audit
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  async getGoogleAdsAuditDebug(accountId: string, params?: {
    days?: number;
    campaign_id?: string;
    ad_group_id?: string;
    search_term?: string;
    save_file?: boolean;
  }): Promise<any> {
    const searchParams = new URLSearchParams();
    if (params?.days) searchParams.append('days', params.days.toString());
    if (params?.campaign_id) searchParams.append('campaign_id', params.campaign_id);
    if (params?.ad_group_id) searchParams.append('ad_group_id', params.ad_group_id);
    if (params?.search_term) searchParams.append('search_term', params.search_term);
    if (params?.save_file) searchParams.append('save_file', 'true');
    const query = searchParams.toString();
    return this.request(`/google-ads/accounts/${accountId}/audit-debug/${query ? `?${query}` : ''}`);
  }


  // Optimization insights from rules engine
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  async getGoogleAdsOptimizationInsights(accountId: string, params?: {
    days?: number;
  }): Promise<any> {
    const searchParams = new URLSearchParams();
    if (params?.days) searchParams.append('days', params.days.toString());
    const query = searchParams.toString();
    return this.request(`/google-ads/accounts/${accountId}/optimization-insights/${query ? `?${query}` : ''}`);
  }

  // Google Ads - Campaign Creation

  async createGoogleAdsFullCampaign(accountId: string, data: {
    campaign_name: string;
    daily_budget: number;
    bidding_strategy: string;
    ad_group_name: string;
    keywords: Array<{ text: string; match_type: string }>;
    headlines: string[];
    descriptions: string[];
    final_url: string;
    cpc_bid?: number;
    target_cpa?: number;
    target_roas?: number;
    path1?: string;
    path2?: string;
    geo_target_ids?: number[];
  }): Promise<{
    campaign: { campaign_id: string; resource_name: string };
    ad_group: { ad_group_id: string; resource_name: string };
    keywords: Array<{ keyword_id: string; text: string; match_type: string }>;
    ad: { ad_id: string; resource_name: string };
    locations_set: number;
    status: string;
    message: string;
  }> {
    return this.request(`/google-ads/accounts/${accountId}/create-full-campaign/`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async createGoogleAdsCampaign(accountId: string, data: {
    campaign_name: string;
    daily_budget: number;
    bidding_strategy?: string;
    target_cpa?: number;
    target_roas?: number;
  }): Promise<{ campaign_id: string; resource_name: string }> {
    return this.request(`/google-ads/accounts/${accountId}/create-campaign/`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async createGoogleAdsAdGroup(accountId: string, data: {
    campaign_resource_name: string;
    ad_group_name: string;
    cpc_bid?: number;
  }): Promise<{ ad_group_id: string; resource_name: string }> {
    return this.request(`/google-ads/accounts/${accountId}/create-ad-group/`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async createGoogleAdsKeywords(accountId: string, data: {
    ad_group_resource_name: string;
    keywords: Array<{ text: string; match_type: string }>;
  }): Promise<{ keywords: Array<{ keyword_id: string; text: string; match_type: string }> }> {
    return this.request(`/google-ads/accounts/${accountId}/create-keywords/`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async createGoogleAdsAd(accountId: string, data: {
    ad_group_resource_name: string;
    headlines: string[];
    descriptions: string[];
    final_url: string;
    path1?: string;
    path2?: string;
  }): Promise<{ ad_id: string; resource_name: string }> {
    return this.request(`/google-ads/accounts/${accountId}/create-ad/`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async searchGoogleAdsLocations(accountId: string, query: string, country?: string): Promise<{
    locations: Array<{
      resource_name: string;
      id: number;
      name: string;
      canonical_name: string;
      target_type: string;
      country_code: string;
    }>;
  }> {
    const params = new URLSearchParams({ q: query });
    if (country) params.append('country', country);
    return this.request(`/google-ads/accounts/${accountId}/search-locations/?${params.toString()}`);
  }

  async getGoogleAdsCampaignBlueprint(campaignId: string): Promise<{
    campaign_name: string;
    daily_budget: number;
    bidding_strategy: string;
    target_cpa: number | null;
    target_roas: number | null;
    ad_group_name: string;
    cpc_bid: number | null;
    keywords: Array<{ text: string; match_type: string }>;
    headlines: string[];
    descriptions: string[];
    final_url: string;
    account_id: string;
  }> {
    return this.request(`/google-ads/campaigns/${campaignId}/blueprint/`);
  }

  // Unified Google OAuth Callback (handles both Google Ads and GBP)
  async googleOAuthCallback(code: string, state: string): Promise<GoogleOAuthCallbackResponse> {
    return this.request('/auth/google/callback/', {
      method: 'POST',
      body: JSON.stringify({ code, state }),
    });
  }

  // Google Business Profile API
  async getGBPAuthUrl(): Promise<{ auth_url: string; state: string }> {
    return this.request('/gbp/auth/');
  }

  async gbpOAuthCallback(code: string, state?: string): Promise<{
    access_token: string;
    refresh_token: string;
    expires_in: number;
    accounts: GBPOAuthAccount[];
  }> {
    return this.request('/gbp/callback/', {
      method: 'POST',
      body: JSON.stringify({ code, state }),
    });
  }

  async linkGBPAccount(data: {
    client_id: string;
    account_name: string;
    display_name: string;
    account_type?: string;
    role?: string;
    access_token: string;
    refresh_token: string;
    expires_in: number;
  }): Promise<GBPAccount> {
    return this.request('/gbp/link/', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async getGBPDashboard(clientId?: string): Promise<GBPDashboard> {
    const params = clientId ? `?client=${clientId}` : '';
    return this.request(`/gbp/dashboard/${params}`);
  }

  async getGBPAccounts(params?: {
    client?: string;
    is_active?: boolean;
    search?: string;
  }): Promise<PaginatedResponse<GBPAccount>> {
    const searchParams = new URLSearchParams();
    if (params?.is_active !== undefined) searchParams.append('is_active', params.is_active.toString());
    if (params?.search) searchParams.append('search', params.search);

    const query = searchParams.toString();
    return this.request(`/gbp/accounts/${query ? `?${query}` : ''}`);
  }

  async getGBPAccount(id: string): Promise<GBPAccount> {
    return this.request(`/gbp/accounts/${id}/`);
  }

  async syncGBPAccount(id: string): Promise<{
    message: string;
    locations_synced: number;
  }> {
    return this.request(`/gbp/accounts/${id}/sync/`, { method: 'POST' });
  }

  async getGBPLocations(params?: {
    client?: string;
    account?: string;
    is_verified?: boolean;
    search?: string;
    ordering?: string;
    page?: number;
  }): Promise<PaginatedResponse<GBPLocation>> {
    const searchParams = new URLSearchParams();
    if (params?.client) searchParams.append('client', params.client);
    if (params?.account) searchParams.append('account', params.account);
    if (params?.is_verified !== undefined) searchParams.append('is_verified', params.is_verified.toString());
    if (params?.search) searchParams.append('search', params.search);
    if (params?.ordering) searchParams.append('ordering', params.ordering);
    if (params?.page) searchParams.append('page', params.page.toString());

    const query = searchParams.toString();
    return this.request(`/gbp/locations/${query ? `?${query}` : ''}`);
  }

  async getGBPLocation(id: string): Promise<GBPLocation> {
    return this.request(`/gbp/locations/${id}/`);
  }

  async getGBPLocationInsights(id: string, days?: number): Promise<GBPInsights> {
    const params = days ? `?days=${days}` : '';
    return this.request(`/gbp/locations/${id}/insights/${params}`);
  }

  async getGBPLocationInsightsDaily(id: string, days?: number): Promise<GBPDailyInsight[]> {
    const params = days ? `?days=${days}` : '';
    return this.request(`/gbp/locations/${id}/insights/daily/${params}`);
  }

  async getGBPReviews(params?: {
    client?: string;
    location?: string;
    star_rating?: string;
    has_reply?: boolean;
    search?: string;
    ordering?: string;
    page?: number;
  }): Promise<PaginatedResponse<GBPReview>> {
    const searchParams = new URLSearchParams();
    if (params?.client) searchParams.append('client', params.client);
    if (params?.location) searchParams.append('location', params.location);
    if (params?.star_rating && params.star_rating !== 'all') searchParams.append('star_rating', params.star_rating);
    if (params?.has_reply !== undefined) searchParams.append('has_reply', params.has_reply.toString());
    if (params?.search) searchParams.append('search', params.search);
    if (params?.ordering) searchParams.append('ordering', params.ordering);
    if (params?.page) searchParams.append('page', params.page.toString());

    const query = searchParams.toString();
    return this.request(`/gbp/reviews/${query ? `?${query}` : ''}`);
  }

  async getGBPReview(id: string): Promise<GBPReview> {
    return this.request(`/gbp/reviews/${id}/`);
  }

  async replyToGBPReview(id: string, reply: string): Promise<GBPReview> {
    return this.request(`/gbp/reviews/${id}/reply/`, {
      method: 'POST',
      body: JSON.stringify({ reply }),
    });
  }

  async getGBPPosts(params?: {
    client?: string;
    location?: string;
    topic_type?: string;
    state?: string;
    search?: string;
    ordering?: string;
    page?: number;
  }): Promise<PaginatedResponse<GBPPost>> {
    const searchParams = new URLSearchParams();
    if (params?.client) searchParams.append('client', params.client);
    if (params?.location) searchParams.append('location', params.location);
    if (params?.topic_type && params.topic_type !== 'all') searchParams.append('topic_type', params.topic_type);
    if (params?.state && params.state !== 'all') searchParams.append('state', params.state);
    if (params?.search) searchParams.append('search', params.search);
    if (params?.ordering) searchParams.append('ordering', params.ordering);
    if (params?.page) searchParams.append('page', params.page.toString());

    const query = searchParams.toString();
    return this.request(`/gbp/posts/${query ? `?${query}` : ''}`);
  }

  async createGBPLocation(accountId: string, data: {
    title: string;
    phone?: string;
    website?: string;
    primary_category_name?: string;
    service_areas?: string[];
    description?: string;
    regular_hours?: Array<{ day: string; open_time: number; close_time: number }>;
  }): Promise<GBPLocation> {
    return this.request(`/gbp/accounts/${accountId}/locations/create/`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async createGBPPost(locationId: string, data: {
    summary: string;
    topic_type?: string;
    call_to_action?: { actionType: string; url: string };
    media?: { mediaFormat: string; sourceUrl: string }[];
  }): Promise<GBPPost> {
    return this.request(`/gbp/locations/${locationId}/posts/`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async getGBPPhotos(params?: {
    client?: string;
    location?: string;
    category?: string;
    media_format?: string;
    ordering?: string;
    page?: number;
  }): Promise<PaginatedResponse<GBPPhoto>> {
    const searchParams = new URLSearchParams();
    if (params?.client) searchParams.append('client', params.client);
    if (params?.location) searchParams.append('location', params.location);
    if (params?.category && params.category !== 'all') searchParams.append('category', params.category);
    if (params?.media_format && params.media_format !== 'all') searchParams.append('media_format', params.media_format);
    if (params?.ordering) searchParams.append('ordering', params.ordering);
    if (params?.page) searchParams.append('page', params.page.toString());

    const query = searchParams.toString();
    return this.request(`/gbp/photos/${query ? `?${query}` : ''}`);
  }

  async getGBPQuestions(params?: {
    client?: string;
    location?: string;
    search?: string;
    ordering?: string;
    page?: number;
  }): Promise<PaginatedResponse<GBPQuestion>> {
    const searchParams = new URLSearchParams();
    if (params?.client) searchParams.append('client', params.client);
    if (params?.location) searchParams.append('location', params.location);
    if (params?.search) searchParams.append('search', params.search);
    if (params?.ordering) searchParams.append('ordering', params.ordering);
    if (params?.page) searchParams.append('page', params.page.toString());

    const query = searchParams.toString();
    return this.request(`/gbp/questions/${query ? `?${query}` : ''}`);
  }

  async answerGBPQuestion(id: string, answer: string): Promise<GBPQuestion> {
    return this.request(`/gbp/questions/${id}/answer/`, {
      method: 'POST',
      body: JSON.stringify({ answer }),
    });
  }

  // Google Search Console API
  async getGSCAuthUrl(): Promise<{ auth_url: string; state: string }> {
    return this.request('/gsc/auth/');
  }

  async linkGSCProperty(data: {
    client_id: string;
    site_url: string;
    display_name: string;
    permission_level?: string;
    access_token: string;
    refresh_token: string;
    expires_in: number;
  }): Promise<GSCProperty> {
    return this.request('/gsc/link/', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async getGSCDashboard(clientId?: string): Promise<GSCDashboard> {
    const params = clientId ? `?client=${clientId}` : '';
    return this.request(`/gsc/dashboard/${params}`);
  }

  async getGSCProperties(params?: {
    client?: string;
    is_active?: boolean;
    search?: string;
    page?: number;
  }): Promise<PaginatedResponse<GSCProperty>> {
    const searchParams = new URLSearchParams();
    if (params?.is_active !== undefined) searchParams.append('is_active', params.is_active.toString());
    if (params?.search) searchParams.append('search', params.search);
    if (params?.page) searchParams.append('page', params.page.toString());

    const query = searchParams.toString();
    return this.request(`/gsc/properties/${query ? `?${query}` : ''}`);
  }

  async getGSCProperty(id: string): Promise<GSCProperty> {
    return this.request(`/gsc/properties/${id}/`);
  }

  async syncGSCProperty(id: string, days?: number): Promise<{
    message: string;
    queries_synced: number;
    pages_synced: number;
  }> {
    const params = days ? `?days=${days}` : '';
    return this.request(`/gsc/properties/${id}/sync/${params}`, { method: 'POST' });
  }

  async getGSCPropertyAnalytics(id: string, days?: number): Promise<GSCAnalytics> {
    const params = days ? `?days=${days}` : '';
    return this.request(`/gsc/properties/${id}/analytics/${params}`);
  }

  async getGSCSitemaps(propertyId: string): Promise<{
    sitemaps: GSCSitemap[];
    count: number;
  }> {
    return this.request(`/gsc/properties/${propertyId}/sitemaps/`);
  }

  // Facebook Ads API
  async getFacebookAdsAuthUrl(): Promise<{ auth_url: string; state: string }> {
    return this.request('/facebook-ads/auth/');
  }

  async facebookAdsOAuthCallback(code: string, state?: string): Promise<GoogleOAuthCallbackResponse> {
    return this.request('/facebook-ads/callback/', {
      method: 'POST',
      body: JSON.stringify({ code, state }),
    });
  }

  async linkFacebookAdsAccount(data: {
    client_id: string;
    account_id: string;
    name: string;
    access_token: string;
    token_type?: string;
    expires_in?: number;
    currency?: string;
    timezone_name?: string;
    business_id?: string;
  }): Promise<FacebookAdsAccount> {
    return this.request('/facebook-ads/link/', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async getFacebookAdsDashboard(clientId?: string): Promise<FacebookAdsDashboard> {
    const params = clientId ? `?client=${clientId}` : '';
    return this.request(`/facebook-ads/dashboard/${params}`);
  }

  async getFacebookAdsAccounts(params?: {
    client?: string;
    is_active?: boolean;
    search?: string;
    page?: number;
  }): Promise<PaginatedResponse<FacebookAdsAccount>> {
    const searchParams = new URLSearchParams();
    if (params?.client) searchParams.append('client', params.client);
    if (params?.is_active !== undefined) searchParams.append('is_active', params.is_active.toString());
    if (params?.search) searchParams.append('search', params.search);
    if (params?.page) searchParams.append('page', params.page.toString());

    const query = searchParams.toString();
    return this.request(`/facebook-ads/accounts/${query ? `?${query}` : ''}`);
  }

  async getFacebookAdsAccount(id: string): Promise<FacebookAdsAccount> {
    return this.request(`/facebook-ads/accounts/${id}/`);
  }

  async syncFacebookAdsAccount(id: string): Promise<{
    message: string;
    summary: Record<string, unknown>;
    campaigns_synced: number;
    ad_sets_synced: number;
  }> {
    return this.request(`/facebook-ads/accounts/${id}/sync/`, { method: 'POST' });
  }

  async getFacebookAdsAccountAnalytics(id: string, days?: number, breakdown?: string): Promise<FacebookAdsAnalytics> {
    const searchParams = new URLSearchParams();
    if (days) searchParams.append('days', days.toString());
    if (breakdown) searchParams.append('breakdown', breakdown);
    const query = searchParams.toString();
    return this.request(`/facebook-ads/accounts/${id}/analytics/${query ? `?${query}` : ''}`);
  }

  async getFacebookAdsPerformanceBreakdown(id: string, breakdown: string, days?: number): Promise<{
    breakdown: string;
    data: Array<Record<string, unknown>>;
  }> {
    const searchParams = new URLSearchParams();
    searchParams.append('breakdown', breakdown);
    if (days) searchParams.append('days', days.toString());
    return this.request(`/facebook-ads/accounts/${id}/breakdown/?${searchParams.toString()}`);
  }

  async updateFacebookAdsStatus(accountId: string, data: {
    entity_type: 'campaign' | 'adset' | 'ad';
    entity_id: string;
    status: 'ACTIVE' | 'PAUSED';
  }): Promise<{ message: string }> {
    return this.request(`/facebook-ads/accounts/${accountId}/update-status/`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async getFacebookAdsCampaigns(params?: {
    account?: string;
    status?: string;
    objective?: string;
    search?: string;
    page?: number;
  }): Promise<PaginatedResponse<FacebookAdsCampaign>> {
    const searchParams = new URLSearchParams();
    if (params?.account) searchParams.append('account', params.account);
    if (params?.status) searchParams.append('status', params.status);
    if (params?.objective) searchParams.append('objective', params.objective);
    if (params?.search) searchParams.append('search', params.search);
    if (params?.page) searchParams.append('page', params.page.toString());

    const query = searchParams.toString();
    return this.request(`/facebook-ads/campaigns/${query ? `?${query}` : ''}`);
  }

  async getFacebookAdsCampaign(id: string): Promise<FacebookAdsCampaign> {
    return this.request(`/facebook-ads/campaigns/${id}/`);
  }

  async getFacebookAdsAdSets(params?: {
    campaign?: string;
    status?: string;
    search?: string;
    page?: number;
  }): Promise<PaginatedResponse<FacebookAdsAdSet>> {
    const searchParams = new URLSearchParams();
    if (params?.campaign) searchParams.append('campaign', params.campaign);
    if (params?.status) searchParams.append('status', params.status);
    if (params?.search) searchParams.append('search', params.search);
    if (params?.page) searchParams.append('page', params.page.toString());

    const query = searchParams.toString();
    return this.request(`/facebook-ads/adsets/${query ? `?${query}` : ''}`);
  }

  async getFacebookAdsAdSet(id: string): Promise<FacebookAdsAdSet> {
    return this.request(`/facebook-ads/adsets/${id}/`);
  }

  async getFacebookAdsAds(params?: {
    ad_set?: string;
    status?: string;
    review_feedback?: string;
    search?: string;
    page?: number;
  }): Promise<PaginatedResponse<FacebookAdsAd>> {
    const searchParams = new URLSearchParams();
    if (params?.ad_set) searchParams.append('ad_set', params.ad_set);
    if (params?.status) searchParams.append('status', params.status);
    if (params?.review_feedback) searchParams.append('review_feedback', params.review_feedback);
    if (params?.search) searchParams.append('search', params.search);
    if (params?.page) searchParams.append('page', params.page.toString());

    const query = searchParams.toString();
    return this.request(`/facebook-ads/ads/${query ? `?${query}` : ''}`);
  }

  async getFacebookAdsAd(id: string): Promise<FacebookAdsAd> {
    return this.request(`/facebook-ads/ads/${id}/`);
  }

  // Candidates (Hiring)
  async getCandidates(params?: {
    status?: string;
    department?: string;
    search?: string;
    page?: number;
  }): Promise<PaginatedResponse<Candidate>> {
    const searchParams = new URLSearchParams();
    if (params?.status && params.status !== 'all') searchParams.append('status', params.status);
    if (params?.department && params.department !== 'all') searchParams.append('department', params.department);
    if (params?.search) searchParams.append('search', params.search);
    if (params?.page) searchParams.append('page', params.page.toString());

    const query = searchParams.toString();
    return this.request(`/candidates/${query ? `?${query}` : ''}`);
  }

  async getCandidate(id: string): Promise<Candidate> {
    return this.request(`/candidates/${id}/`);
  }

  async createCandidate(data: Partial<Candidate>): Promise<Candidate> {
    return this.request('/candidates/', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateCandidate(id: string, data: Partial<Candidate>): Promise<Candidate> {
    return this.request(`/candidates/${id}/`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteCandidate(id: string): Promise<void> {
    await this.request(`/candidates/${id}/`, { method: 'DELETE' });
  }

  // Support Requests
  async getSupportRequests(params?: {
    status?: string;
    priority?: string;
    request_type?: string;
    search?: string;
    page?: number;
  }): Promise<PaginatedResponse<SupportRequest>> {
    const searchParams = new URLSearchParams();
    if (params?.status && params.status !== 'all') searchParams.append('status', params.status);
    if (params?.priority && params.priority !== 'all') searchParams.append('priority', params.priority);
    if (params?.request_type && params.request_type !== 'all') searchParams.append('request_type', params.request_type);
    if (params?.search) searchParams.append('search', params.search);
    if (params?.page) searchParams.append('page', params.page.toString());

    const query = searchParams.toString();
    return this.request(`/requests/${query ? `?${query}` : ''}`);
  }

  async getSupportRequest(id: string): Promise<SupportRequest> {
    return this.request(`/requests/${id}/`);
  }

  async createSupportRequest(data: Partial<SupportRequest>): Promise<SupportRequest> {
    return this.request('/requests/', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateSupportRequest(id: string, data: Partial<SupportRequest>): Promise<SupportRequest> {
    return this.request(`/requests/${id}/`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteSupportRequest(id: string): Promise<void> {
    await this.request(`/requests/${id}/`, { method: 'DELETE' });
  }

  // Services CRUD
  async getService(id: string): Promise<Service> {
    return this.request(`/services/${id}/`);
  }

  async createService(data: Partial<Service>): Promise<Service> {
    return this.request('/services/', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateService(id: string, data: Partial<Service>): Promise<Service> {
    return this.request(`/services/${id}/`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteService(id: string): Promise<void> {
    await this.request(`/services/${id}/`, { method: 'DELETE' });
  }

  // ==========================================================================
  // TRACKING SYSTEMS - Bugs, Features, Research, Ideas
  // ==========================================================================

  // Tracking Stats
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  async getTrackingStats(): Promise<any> {
    return this.request('/tracking/stats/');
  }

  // Bugs
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  async getBugs(params?: { status?: string; severity?: string; category?: string; search?: string }): Promise<any> {
    const searchParams = new URLSearchParams();
    if (params?.status) searchParams.append('status', params.status);
    if (params?.severity) searchParams.append('severity', params.severity);
    if (params?.category) searchParams.append('category', params.category);
    if (params?.search) searchParams.append('search', params.search);
    const query = searchParams.toString();
    return this.request(`/tracking/bugs/${query ? `?${query}` : ''}`);
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  async createBug(data: any): Promise<any> {
    return this.request('/tracking/bugs/', { method: 'POST', body: JSON.stringify(data) });
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  async updateBug(id: number, data: any): Promise<any> {
    return this.request(`/tracking/bugs/${id}/`, { method: 'PATCH', body: JSON.stringify(data) });
  }

  async deleteBug(id: number): Promise<void> {
    return this.request(`/tracking/bugs/${id}/`, { method: 'DELETE' });
  }

  // Ideas
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  async getIdeas(params?: { status?: string; priority?: string; category?: string; search?: string }): Promise<any> {
    const searchParams = new URLSearchParams();
    if (params?.status) searchParams.append('status', params.status);
    if (params?.priority) searchParams.append('priority', params.priority);
    if (params?.category) searchParams.append('category', params.category);
    if (params?.search) searchParams.append('search', params.search);
    const query = searchParams.toString();
    return this.request(`/tracking/ideas/${query ? `?${query}` : ''}`);
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  async createIdea(data: any): Promise<any> {
    return this.request('/tracking/ideas/', { method: 'POST', body: JSON.stringify(data) });
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  async updateIdea(id: number, data: any): Promise<any> {
    return this.request(`/tracking/ideas/${id}/`, { method: 'PATCH', body: JSON.stringify(data) });
  }

  async deleteIdea(id: number): Promise<void> {
    return this.request(`/tracking/ideas/${id}/`, { method: 'DELETE' });
  }

  // Features
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  async getFeatures(params?: { status?: string; category?: string; search?: string }): Promise<any> {
    const searchParams = new URLSearchParams();
    if (params?.status) searchParams.append('status', params.status);
    if (params?.category) searchParams.append('category', params.category);
    if (params?.search) searchParams.append('search', params.search);
    const query = searchParams.toString();
    return this.request(`/tracking/features/${query ? `?${query}` : ''}`);
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  async createFeature(data: any): Promise<any> {
    return this.request('/tracking/features/', { method: 'POST', body: JSON.stringify(data) });
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  async updateFeature(id: number, data: any): Promise<any> {
    return this.request(`/tracking/features/${id}/`, { method: 'PATCH', body: JSON.stringify(data) });
  }

  async deleteFeature(id: number): Promise<void> {
    return this.request(`/tracking/features/${id}/`, { method: 'DELETE' });
  }

  // Research
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  async getResearch(params?: { status?: string; category?: string; area?: string; search?: string }): Promise<any> {
    const searchParams = new URLSearchParams();
    if (params?.status) searchParams.append('status', params.status);
    if (params?.category) searchParams.append('category', params.category);
    if (params?.area) searchParams.append('area', params.area);
    if (params?.search) searchParams.append('search', params.search);
    const query = searchParams.toString();
    return this.request(`/tracking/research/${query ? `?${query}` : ''}`);
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  async createResearch(data: any): Promise<any> {
    return this.request('/tracking/research/', { method: 'POST', body: JSON.stringify(data) });
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  async updateResearch(id: number, data: any): Promise<any> {
    return this.request(`/tracking/research/${id}/`, { method: 'PATCH', body: JSON.stringify(data) });
  }

  async deleteResearch(id: number): Promise<void> {
    return this.request(`/tracking/research/${id}/`, { method: 'DELETE' });
  }

  // Sales Pipeline
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  async getDeals(params?: { stage?: string; source?: string; assigned_to?: string; search?: string }): Promise<any> {
    const searchParams = new URLSearchParams();
    if (params?.stage && params.stage !== 'all') searchParams.append('stage', params.stage);
    if (params?.source && params.source !== 'all') searchParams.append('source', params.source);
    if (params?.assigned_to && params.assigned_to !== 'all') searchParams.append('assigned_to', params.assigned_to);
    if (params?.search) searchParams.append('search', params.search);
    const query = searchParams.toString();
    return this.request(`/deals/${query ? `?${query}` : ''}`);
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  async getDeal(id: string): Promise<any> {
    return this.request(`/deals/${id}/`);
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  async createDeal(data: any): Promise<any> {
    return this.request('/deals/', { method: 'POST', body: JSON.stringify(data) });
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  async updateDeal(id: string, data: any): Promise<any> {
    return this.request(`/deals/${id}/`, { method: 'PUT', body: JSON.stringify(data) });
  }

  async deleteDeal(id: string): Promise<void> {
    return this.request(`/deals/${id}/`, { method: 'DELETE' });
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  async moveDealStage(id: string, stage: string, lostReason?: string): Promise<any> {
    const body: any = { stage };
    if (lostReason) body.lost_reason = lostReason;
    return this.request(`/deals/${id}/move-stage/`, { method: 'PATCH', body: JSON.stringify(body) });
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  async getDealActivities(dealId: string): Promise<any> {
    return this.request(`/deals/${dealId}/activities/`);
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  async createDealActivity(dealId: string, data: any): Promise<any> {
    return this.request(`/deals/${dealId}/activities/`, { method: 'POST', body: JSON.stringify(data) });
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  async getPipelineStats(): Promise<any> {
    return this.request('/pipeline/stats/');
  }

  // ============================================================
  // Email Management
  // ============================================================

  async getEmailAccounts(): Promise<EmailAccount[]> {
    return this.request('/email/accounts/');
  }

  async createEmailAccount(data: {
    email_address: string;
    password: string;
    display_name?: string;
    imap_host?: string;
    imap_port?: number;
    smtp_host?: string;
    smtp_port?: number;
  }): Promise<EmailAccount> {
    return this.request('/email/accounts/', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async deleteEmailAccount(id: string): Promise<void> {
    await this.request(`/email/accounts/${id}/`, { method: 'DELETE' });
  }

  async syncEmailAccount(id: string, folder?: string): Promise<{ synced: number; folder: string; last_synced: string }> {
    return this.request(`/email/accounts/${id}/sync/`, {
      method: 'POST',
      body: JSON.stringify({ folder: folder || 'INBOX' }),
    });
  }

  async getEmailFolders(accountId: string): Promise<EmailFolder[]> {
    return this.request(`/email/accounts/${accountId}/folders/`);
  }

  async getEmailMessages(accountId: string, params?: {
    folder?: string;
    search?: string;
    page?: number;
    page_size?: number;
  }): Promise<{ count: number; results: EmailMessageSummary[]; page: number; page_size: number }> {
    const searchParams = new URLSearchParams();
    if (params?.folder) searchParams.append('folder', params.folder);
    if (params?.search) searchParams.append('search', params.search);
    if (params?.page) searchParams.append('page', params.page.toString());
    if (params?.page_size) searchParams.append('page_size', params.page_size.toString());
    const query = searchParams.toString();
    return this.request(`/email/accounts/${accountId}/messages/${query ? `?${query}` : ''}`);
  }

  async getEmailMessage(accountId: string, messageId: string): Promise<EmailMessageDetail> {
    return this.request(`/email/accounts/${accountId}/messages/${messageId}/`);
  }

  async updateEmailMessage(accountId: string, messageId: string, data: { is_read?: boolean; is_starred?: boolean }): Promise<EmailMessageSummary> {
    return this.request(`/email/accounts/${accountId}/messages/${messageId}/update/`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async deleteEmailMessage(accountId: string, messageId: string): Promise<void> {
    await this.request(`/email/accounts/${accountId}/messages/${messageId}/delete/`, { method: 'DELETE' });
  }

  async sendEmail(accountId: string, data: {
    to: string[];
    cc?: string[];
    bcc?: string[];
    subject: string;
    body: string;
    in_reply_to?: string;
    references?: string;
  }): Promise<{ message_id: string; status: string }> {
    return this.request(`/email/accounts/${accountId}/send/`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  // ==========================================================================
  // SEO Stack
  // ==========================================================================

  async getSEOCrawlConfigs(clientId?: string): Promise<CrawlConfig[]> {
    const params = clientId ? `?client=${clientId}` : '';
    return this.request(`/seo/crawl-config/${params}`);
  }

  async createSEOCrawlConfig(data: Partial<CrawlConfig>): Promise<CrawlConfig> {
    return this.request('/seo/crawl-config/', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateSEOCrawlConfig(id: string, data: Partial<CrawlConfig>): Promise<CrawlConfig> {
    return this.request(`/seo/crawl-config/${id}/`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteSEOCrawlConfig(id: string): Promise<void> {
    await this.request(`/seo/crawl-config/${id}/`, { method: 'DELETE' });
  }

  async triggerCrawl(clientId: string): Promise<{ task_id: string; crawl_run_id: string }> {
    return this.request('/seo/crawl/', {
      method: 'POST',
      body: JSON.stringify({ client_id: clientId }),
    });
  }

  async getSEOCrawlRuns(clientId?: string): Promise<{ results: CrawlRun[] }> {
    const params = clientId ? `?client=${clientId}` : '';
    return this.request(`/seo/crawl-runs/${params}`);
  }

  async getSEOCrawlRunDetail(id: string): Promise<CrawlRun> {
    return this.request(`/seo/crawl-runs/${id}/`);
  }

  async getSEOCrawlRunPages(crawlRunId: string): Promise<{ results: CrawledPage[] }> {
    return this.request(`/seo/crawl-runs/${crawlRunId}/pages/`);
  }

  async getSEOCrawlRunStatus(id: string): Promise<{ id: string; status: string; pages_crawled: number; pages_errored: number; started_at: string }> {
    return this.request(`/seo/crawl-runs/${id}/status/`);
  }

  async getSEOFindings(params?: {
    client?: string;
    engine?: string;
    severity?: string;
    is_resolved?: boolean;
  }): Promise<{ results: SEOFinding[] }> {
    const searchParams = new URLSearchParams();
    if (params?.client) searchParams.append('client', params.client);
    if (params?.engine) searchParams.append('engine', params.engine);
    if (params?.severity) searchParams.append('severity', params.severity);
    if (params?.is_resolved !== undefined) searchParams.append('is_resolved', params.is_resolved.toString());
    const query = searchParams.toString();
    return this.request(`/seo/findings/${query ? `?${query}` : ''}`);
  }

  async resolveSEOFinding(id: string): Promise<SEOFinding> {
    return this.request(`/seo/findings/${id}/resolve/`, { method: 'POST' });
  }

  async suppressSEOFinding(id: string): Promise<SEOFinding> {
    return this.request(`/seo/findings/${id}/suppress/`, { method: 'POST' });
  }

  async getSEOChangeEvents(clientId?: string): Promise<{ results: SEOChangeEvent[] }> {
    const params = clientId ? `?client=${clientId}` : '';
    return this.request(`/seo/change-events/${params}`);
  }

  async getSEOLinkEquity(params?: { client?: string; crawl_run?: string }): Promise<{ results: LinkEquityScore[] }> {
    const searchParams = new URLSearchParams();
    if (params?.client) searchParams.append('client', params.client);
    if (params?.crawl_run) searchParams.append('crawl_run', params.crawl_run);
    const query = searchParams.toString();
    return this.request(`/seo/link-equity/${query ? `?${query}` : ''}`);
  }

  async getSEOKeywordMappings(clientId: string): Promise<{ results: KeywordPageMapping[] }> {
    return this.request(`/seo/keyword-mappings/?client=${clientId}`);
  }

  async getSEOCoverageGaps(params?: { client?: string; gap_type?: string }): Promise<{ results: CoverageGap[] }> {
    const searchParams = new URLSearchParams();
    if (params?.client) searchParams.append('client', params.client);
    if (params?.gap_type) searchParams.append('gap_type', params.gap_type);
    const query = searchParams.toString();
    return this.request(`/seo/coverage-gaps/${query ? `?${query}` : ''}`);
  }

  async getSEOPriorities(clientId: string): Promise<{ results: SEOFinding[] }> {
    return this.request(`/seo/priorities/?client=${clientId}`);
  }

  async getSEODashboard(clientId: string): Promise<SEODashboard> {
    return this.request(`/seo/dashboard/?client=${clientId}`);
  }

  async getClientSEOSummary(clientId: string): Promise<ClientSEOSummary> {
    return this.request(`/clients/${clientId}/seo/summary/`);
  }


  // Landing Pages
  async getClientLandingPages(clientId: string): Promise<LandingPageConfig[]> {
    return this.request(`/clients/${clientId}/landing-pages/`);
  }

  async createLandingPage(clientId: string, data: LandingPageInput): Promise<LandingPageConfig> {
    return this.request(`/clients/${clientId}/landing-pages/`, {
      method: "POST",
      body: JSON.stringify(data),
    });
  }

  async updateLandingPage(clientId: string, lpId: string, data: Partial<LandingPageInput>): Promise<LandingPageConfig> {
    return this.request(`/clients/${clientId}/landing-pages/${lpId}/`, {
      method: "PATCH",
      body: JSON.stringify(data),
    });
  }

  async deleteLandingPage(clientId: string, lpId: string): Promise<void> {
    return this.request(`/clients/${clientId}/landing-pages/${lpId}/`, {
      method: "DELETE",
    });
  }

  async buildLandingPage(clientId: string, lpId: string): Promise<{ status: string; config_id: string; subdomain: string }> {
    return this.request(`/clients/${clientId}/landing-pages/${lpId}/build/`, {
      method: "POST",
    });
  }

  async triggerClientCrawl(clientId: string): Promise<{ task_id: string; crawl_run_id: string }> {
    return this.request(`/clients/${clientId}/seo/crawl/`, { method: 'POST' });
  }

  // Tracking Numbers
  async getTrackingNumbers(clientId: string): Promise<TrackingNumber[]> {
    const data = await this.request<{ results: TrackingNumber[] } | TrackingNumber[]>(`/tracking-numbers/?client=${clientId}`);
    return Array.isArray(data) ? data : data.results;
  }

  async updateTrackingNumber(id: string, data: Partial<TrackingNumber>): Promise<TrackingNumber> {
    return this.request(`/tracking-numbers/${id}/`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async createTrackingNumber(data: {
    client: string;
    phone_e164: string;
    phone_display?: string;
    forward_to: string;
    purpose?: string;
  }): Promise<TrackingNumber> {
    return this.request('/tracking-numbers/', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  // Call Logs
  async getClientCalls(clientId: string, params?: { start_date?: string; end_date?: string; status?: string }): Promise<CallLogResponse> {
    const queryParams = new URLSearchParams();
    if (params?.start_date) queryParams.append('start_date', params.start_date);
    if (params?.end_date) queryParams.append('end_date', params.end_date);
    if (params?.status) queryParams.append('status', params.status);
    const queryString = queryParams.toString();
    return this.request(`/clients/${clientId}/calls/${queryString ? `?${queryString}` : ''}`);
  }

  // Notification Contacts
  async getNotificationContacts(clientId: string): Promise<ClientNotificationContact[]> {
    const data = await this.request<{ results: ClientNotificationContact[] } | ClientNotificationContact[]>(`/notification-contacts/?client=${clientId}`);
    return Array.isArray(data) ? data : data.results;
  }

  // Client Onboarding
  async getClientOnboarding(clientId: string): Promise<ClientOnboarding> {
    return this.request(`/clients/${clientId}/onboarding/`);
  }

  async updateClientOnboarding(
    clientId: string,
    data: Partial<{ modules_sold: string[]; status: string }>
  ): Promise<ClientOnboarding> {
    return this.request(`/clients/${clientId}/onboarding/`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async updateOnboardingStep(
    clientId: string,
    stepNum: number,
    data: { checklist?: Record<string, unknown>; status?: string }
  ): Promise<ClientOnboarding> {
    return this.request(`/clients/${clientId}/onboarding/step/${stepNum}/`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async createOnboardingTestLead(
    clientId: string,
    data: { name: string; phone: string; email: string }
  ): Promise<{ lead_id: string; message: string; tracking: Record<string, boolean> }> {
    return this.request(`/clients/${clientId}/onboarding/test-lead/`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async runOnboardingValidation(clientId: string): Promise<ValidationResults> {
    return this.request(`/clients/${clientId}/onboarding/validate/`, {
      method: 'POST',
    });
  }

  async sendOnboardingHandoff(
    clientId: string,
    data: { recipient_email: string }
  ): Promise<{ status: string; recipient: string }> {
    return this.request(`/clients/${clientId}/onboarding/send-handoff/`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  // -------------------------------------------------------------------------
  // Billing / Engagements
  // -------------------------------------------------------------------------

  async getEngagements(clientId: string): Promise<Engagement[]> {
    return this.request<Engagement[]>(`/clients/${clientId}/engagements/`);
  }

  async createEngagement(
    clientId: string,
    data: {
      engagement_type: 'initial' | 'cruising';
      amount_cents: number;
      description?: string;
      currency?: string;
      metadata?: Record<string, unknown>;
    }
  ): Promise<Engagement> {
    return this.request<Engagement>(`/clients/${clientId}/engagements/`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateEngagement(
    clientId: string,
    engagementId: string,
    data: Partial<Pick<Engagement, 'description' | 'amount_cents' | 'metadata' | 'status'>>
  ): Promise<Engagement> {
    return this.request<Engagement>(`/clients/${clientId}/engagements/${engagementId}/`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async sendEngagementInvoice(
    clientId: string,
    engagementId: string,
    testEmail?: string
  ): Promise<{ status: string; invoice_url: string; stripe_invoice_id: string; sent_to: string }> {
    return this.request(`/clients/${clientId}/engagements/${engagementId}/send-invoice/`, {
      method: 'POST',
      body: JSON.stringify(testEmail ? { test_email: testEmail } : {}),
    });
  }

  async createCheckoutSession(
    clientId: string,
    engagementId: string,
    data?: { success_url?: string; cancel_url?: string }
  ): Promise<{ status: string; checkout_url: string; stripe_checkout_session_id: string }> {
    return this.request(`/clients/${clientId}/engagements/${engagementId}/checkout/`, {
      method: 'POST',
      body: JSON.stringify(data || {}),
    });
  }

  async getBillingSummary(clientId: string): Promise<BillingSummary> {
    return this.request<BillingSummary>(`/clients/${clientId}/billing-summary/`);
  }

  async getStripeSettings(): Promise<{ mode: string; has_live_keys: boolean; has_test_keys: boolean }> {
    return this.request('/stripe/settings/');
  }

  async setStripeMode(mode: 'test' | 'live'): Promise<{ mode: string; has_live_keys: boolean; has_test_keys: boolean }> {
    return this.request('/stripe/settings/', { method: 'POST', body: JSON.stringify({ mode }) });
  }

  // ===========================================================================
  // PROSPECT/OUTREACH METHODS
  // ===========================================================================

  // Prospect Lists
  async getProspectLists(params?: { search?: string }): Promise<ProspectList[]> {
    const query = params ? new URLSearchParams(params as Record<string, string>).toString() : '';
    return this.request<ProspectList[]>(`/prospect-lists/${query ? `?${query}` : ''}`);
  }

  async getProspectList(id: string): Promise<ProspectListDetail> {
    return this.request<ProspectListDetail>(`/prospect-lists/${id}/`);
  }

  async createProspectList(data: { name: string; description?: string; source?: string }): Promise<ProspectList> {
    return this.request<ProspectList>('/prospect-lists/', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateProspectList(id: string, data: Partial<ProspectList>): Promise<ProspectList> {
    return this.request<ProspectList>(`/prospect-lists/${id}/`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteProspectList(id: string): Promise<void> {
    return this.request(`/prospect-lists/${id}/`, { method: 'DELETE' });
  }

  async importProspectsCSV(listId: string, file: File): Promise<{ success: boolean; import_id: string; total_rows: number; message: string }> {
    const formData = new FormData();
    formData.append('file', file);

    const token = localStorage.getItem('access_token');
    const response = await fetch(`${API_URL}/prospect-lists/${listId}/import/`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
      body: formData,
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Import failed');
    }

    return response.json();
  }

  // Prospect Import Status
  async getProspectImportStatus(importId: string): Promise<ProspectImport> {
    return this.request<ProspectImport>(`/prospect-imports/${importId}/`);
  }

  // Prospects
  async getProspects(params?: {
    prospect_list?: string;
    status?: string;
    category?: string;
    assigned_to?: string;
    priority?: string;
    vertical?: string;
    tier?: string;
    source?: string;
    confidence?: string;
    search?: string;
    city?: string;
    enriched?: string;
    ordering?: string;
    has_financing?: string;
    runs_google_ads?: string;
    has_marketing_tools?: string;
    has_branded_fleet?: string;
    hiring_signals?: string;
  }): Promise<Prospect[]> {
    const query = params ? new URLSearchParams(params as Record<string, string>).toString() : '';
    return this.request<Prospect[]>(`/prospects/${query ? `?${query}` : ''}`);
  }

  async getProspect(id: string): Promise<ProspectDetail> {
    return this.request<ProspectDetail>(`/prospects/${id}/`);
  }

  async createProspect(data: Partial<Prospect>): Promise<Prospect> {
    return this.request<Prospect>('/prospects/', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateProspect(id: string, data: Partial<Prospect>): Promise<Prospect> {
    return this.request<Prospect>(`/prospects/${id}/`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async deleteProspect(id: string): Promise<void> {
    return this.request(`/prospects/${id}/`, { method: 'DELETE' });
  }

  async updateProspectStatus(id: string, status: string): Promise<ProspectDetail> {
    return this.request<ProspectDetail>(`/prospects/${id}/status/`, {
      method: 'PATCH',
      body: JSON.stringify({ status }),
    });
  }

  async convertProspectToDeal(id: string, data?: { deal_value?: number; notes?: string }): Promise<{ success: boolean; deal_id: string; prospect: ProspectDetail }> {
    return this.request(`/prospects/${id}/convert/`, {
      method: 'POST',
      body: JSON.stringify(data || {}),
    });
  }

  // Prospect Activities
  async getProspectActivities(prospectId: string): Promise<ProspectActivity[]> {
    return this.request<ProspectActivity[]>(`/prospects/${prospectId}/activities/`);
  }

  async createProspectActivity(prospectId: string, data: {
    activity_type: string;
    description: string;
    metadata?: Record<string, unknown>;
  }): Promise<ProspectActivity> {
    return this.request<ProspectActivity>(`/prospects/${prospectId}/activities/`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  // Prospect Stats
  async getProspectStats(params?: { prospect_list?: string; assigned_to?: string; vertical?: string }): Promise<ProspectStats> {
    const query = params ? new URLSearchParams(params as Record<string, string>).toString() : '';
    return this.request<ProspectStats>(`/prospects/stats/${query ? `?${query}` : ''}`);
  }

  // Prospect Touches
  async getProspectTouches(prospectId: string): Promise<ProspectTouch[]> {
    return this.request<ProspectTouch[]>(`/prospects/${prospectId}/touches/`);
  }

  async createProspectTouch(prospectId: string, data: {
    channel: string;
    result?: string;
    notes?: string;
    template_id?: string;
    next_step?: string;
    next_action_at?: string;
  }): Promise<ProspectTouch> {
    return this.request<ProspectTouch>(`/prospects/${prospectId}/touches/`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  // Prospect Scoring
  async calculateProspectScore(prospectId: string): Promise<{
    fit_score: number;
    tier: number;
    confidence: string;
    score_reason: string;
    scored_at: string;
  }> {
    return this.request(`/prospects/${prospectId}/score/`, {
      method: 'POST',
    });
  }

  async bulkScoreProspects(params?: {
    prospect_list?: string;
    vertical?: string;
    status?: string;
    limit?: number;
  }): Promise<{ processed: number; updated: number }> {
    return this.request('/prospects/bulk-score/', {
      method: 'POST',
      body: JSON.stringify(params || {}),
    });
  }

  // Enrichment Jobs
  async startEnrichment(prospectId: string): Promise<{
    success: boolean;
    job_id: string;
    message: string;
  }> {
    return this.request(`/prospects/${prospectId}/enrich/`, {
      method: 'POST',
    });
  }

  async getEnrichmentJob(jobId: string): Promise<EnrichmentJob> {
    return this.request<EnrichmentJob>(`/enrichment-jobs/${jobId}/`);
  }

  async approveEnrichment(jobId: string, reviewedData: EnrichmentExtractedData): Promise<{
    success: boolean;
    job_id: string;
    prospect: ProspectDetail;
  }> {
    return this.request(`/enrichment-jobs/${jobId}/approve/`, {
      method: 'POST',
      body: JSON.stringify({ reviewed_data: reviewedData }),
    });
  }

  async rejectEnrichment(jobId: string): Promise<{
    success: boolean;
    job_id: string;
    message: string;
  }> {
    return this.request(`/enrichment-jobs/${jobId}/reject/`, {
      method: 'POST',
    });
  }

  // Scrap.io Google Maps Import (export-based workflow)
  async getScrapioAccountInfo(): Promise<{
    success: boolean;
    credits_remaining: number;
    subscription: Record<string, unknown>;
  }> {
    return this.request('/scrapio/account/');
  }

  async getScrapioBusinessTypes(): Promise<{
    success: boolean;
    types: Record<string, string>;
  }> {
    return this.request('/scrapio/types/');
  }

  async getScrapioUsStates(): Promise<{
    success: boolean;
    states: Array<{ code: string; name: string; city_count: number }>;
  }> {
    return this.request('/scrapio/states/');
  }

  async getScrapioStateCities(stateCode: string): Promise<{
    success: boolean;
    state_code: string;
    state_name: string;
    cities: string[];
    city_count: number;
  }> {
    return this.request(`/scrapio/states/${stateCode}/cities/`);
  }

  async scrapioSearchPreview(params: {
    business_type: string;
    city: string;
  }): Promise<{
    success: boolean;
    message: string;
    business_type: string;
    city: string;
    credits_remaining: number;
    note: string;
  }> {
    return this.request('/scrapio/preview/', {
      method: 'POST',
      body: JSON.stringify(params),
    });
  }

  async scrapioStartImport(params: {
    prospect_list_id?: string;
    list_name?: string;
    list_description?: string;
    business_type: string;
    city?: string;
    state_code?: string;
    max_results?: number;
    max_results_per_city?: number;
    max_cities?: number;
  }): Promise<{
    success: boolean;
    import_id: string;
    prospect_list_id: string;
    mode: 'city' | 'state';
    message: string;
  }> {
    return this.request('/scrapio/import/', {
      method: 'POST',
      body: JSON.stringify(params),
    });
  }

  // ==========================================================================
  // LINK TRACKING
  // ==========================================================================

  async getTrackableLinks(params?: {
    campaign?: string;
    prospect?: string;
    has_clicks?: boolean;
    search?: string;
  }): Promise<TrackableLink[]> {
    const queryParams = new URLSearchParams();
    if (params?.campaign) queryParams.append('campaign', params.campaign);
    if (params?.prospect) queryParams.append('prospect', params.prospect);
    if (params?.has_clicks !== undefined) queryParams.append('has_clicks', String(params.has_clicks));
    if (params?.search) queryParams.append('search', params.search);
    const query = queryParams.toString();
    return this.request(`/trackable-links/${query ? '?' + query : ''}`);
  }

  async createTrackableLink(data: {
    target_url: string;
    prospect_id?: string;
    contact_email?: string;
    contact_name?: string;
    company_name?: string;
    campaign?: string;
    notes?: string;
  }): Promise<TrackableLink> {
    return this.request('/trackable-links/', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async bulkCreateTrackableLinks(data: {
    target_url: string;
    prospect_ids: string[];
    campaign?: string;
  }): Promise<{ success: boolean; created_count: number; links: TrackableLink[] }> {
    return this.request('/trackable-links/bulk/', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async getTrackableLinkStats(campaign?: string): Promise<TrackableLinkStats> {
    const query = campaign ? `?campaign=${encodeURIComponent(campaign)}` : '';
    return this.request(`/trackable-links/stats/${query}`);
  }

  async getTrackableLinkClicks(linkId: string): Promise<LinkClick[]> {
    return this.request(`/trackable-links/${linkId}/clicks/`);
  }

  // ==========================================================================
  // PROSPECT CONTACTS (Decision Makers)
  // ==========================================================================

  async getProspectContacts(prospectId: string): Promise<ProspectContact[]> {
    return this.request(`/prospects/${prospectId}/contacts/`);
  }

  async createProspectContact(prospectId: string, data: {
    first_name: string;
    last_name?: string;
    title?: string;
    title_category?: string;
    email?: string;
    phone?: string;
    mobile?: string;
    linkedin_url?: string;
    is_decision_maker?: boolean;
    is_primary?: boolean;
    source?: string;
    confidence?: string;
    notes?: string;
  }): Promise<ProspectContact> {
    return this.request(`/prospects/${prospectId}/contacts/`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async getAllContacts(params?: {
    prospect?: string;
    status?: string;
    is_decision_maker?: boolean;
    is_primary?: boolean;
    source?: string;
    search?: string;
  }): Promise<ProspectContact[]> {
    const queryParams = new URLSearchParams();
    if (params?.prospect) queryParams.append('prospect', params.prospect);
    if (params?.status) queryParams.append('status', params.status);
    if (params?.is_decision_maker !== undefined) queryParams.append('is_decision_maker', String(params.is_decision_maker));
    if (params?.is_primary !== undefined) queryParams.append('is_primary', String(params.is_primary));
    if (params?.source) queryParams.append('source', params.source);
    if (params?.search) queryParams.append('search', params.search);
    const query = queryParams.toString();
    return this.request(`/contacts/${query ? '?' + query : ''}`);
  }

  async getContact(contactId: string): Promise<ProspectContact> {
    return this.request(`/contacts/${contactId}/`);
  }

  async updateContact(contactId: string, data: Partial<ProspectContact>): Promise<ProspectContact> {
    return this.request(`/contacts/${contactId}/`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async deleteContact(contactId: string): Promise<void> {
    return this.request(`/contacts/${contactId}/`, { method: 'DELETE' });
  }

  async updateContactStatus(contactId: string, status: string): Promise<ProspectContact> {
    return this.request(`/contacts/${contactId}/status/`, {
      method: 'PATCH',
      body: JSON.stringify({ status }),
    });
  }

  async setContactPrimary(contactId: string): Promise<ProspectContact> {
    return this.request(`/contacts/${contactId}/set-primary/`, { method: 'POST' });
  }

  async getContactStats(prospectListId?: string): Promise<ProspectContactStats> {
    const query = prospectListId ? `?prospect_list=${prospectListId}` : '';
    return this.request(`/contacts/stats/${query}`);
  }

  // Lead Generation
  async getLeadGenProperties(): Promise<LeadGenProperty[]> {
    return this.request<LeadGenProperty[]>('/leadgen/properties/');
  }

  async getLeadGenLeads(params?: LeadGenLeadsParams): Promise<{
    results: LeadGenLead[];
    count: number;
    page: number;
    page_size: number;
    total_pages: number;
  }> {
    const query = params ? new URLSearchParams(params as Record<string, string>).toString() : '';
    return this.request(`/leadgen/leads/${query ? `?${query}` : ''}`);
  }

  async getLeadGenDashboard(propertyId?: string): Promise<LeadGenDashboardData> {
    const query = propertyId ? `?property=${propertyId}` : '';
    return this.request<LeadGenDashboardData>(`/leadgen/dashboard/${query}`);
  }

  async updateLeadGenLeadStatus(leadId: string, data: { status?: string; notes?: string }): Promise<LeadGenLead> {
    return this.request<LeadGenLead>(`/leadgen/leads/${leadId}/`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  // ==========================================================================
  // CONTACT SUBMISSIONS (Digimint website contact form)
  // ==========================================================================

  async getContactSubmissions(params?: {
    status?: string;
    search?: string;
  }): Promise<ContactSubmission[]> {
    const queryParams = new URLSearchParams();
    if (params?.status) queryParams.append('status', params.status);
    if (params?.search) queryParams.append('search', params.search);
    const query = queryParams.toString();
    return this.request(`/contact-submissions/${query ? '?' + query : ''}`);
  }

  async getContactSubmission(id: string): Promise<ContactSubmission> {
    return this.request(`/contact-submissions/${id}/`);
  }

  async updateContactSubmission(id: string, data: Partial<ContactSubmission>): Promise<ContactSubmission> {
    return this.request(`/contact-submissions/${id}/`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async getContactSubmissionStats(): Promise<ContactSubmissionStats> {
    return this.request('/contact-submissions/stats/');
  }

  async convertContactSubmissionToDeal(id: string): Promise<{
    success: boolean;
    deal_id: string;
    message: string;
  }> {
    return this.request(`/contact-submissions/${id}/convert/`, {
      method: 'POST',
    });
  }

  // ===========================================================================
  // COLD EMAIL CAMPAIGNS (Instantly.ai)
  // ===========================================================================

  async getEmailCampaigns(): Promise<EmailCampaign[]> {
    return this.request('/email-campaigns/');
  }

  async getEmailCampaign(id: string): Promise<EmailCampaignDetail> {
    return this.request(`/email-campaigns/${id}/`);
  }

  async createEmailCampaign(data: CreateEmailCampaignRequest): Promise<EmailCampaign> {
    return this.request('/email-campaigns/', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async deleteEmailCampaign(id: string): Promise<void> {
    return this.request(`/email-campaigns/${id}/`, {
      method: 'DELETE',
    });
  }

  async activateEmailCampaign(id: string): Promise<{ success: boolean; status: string }> {
    return this.request(`/email-campaigns/${id}/activate/`, {
      method: 'POST',
    });
  }

  async pauseEmailCampaign(id: string): Promise<{ success: boolean; status: string }> {
    return this.request(`/email-campaigns/${id}/pause/`, {
      method: 'POST',
    });
  }

  async enrollContactsInCampaign(campaignId: string, contactIds: string[]): Promise<{
    success: boolean;
    enrolled: number;
    instantly_result: any;
  }> {
    return this.request(`/email-campaigns/${campaignId}/enroll/`, {
      method: 'POST',
      body: JSON.stringify({ contact_ids: contactIds }),
    });
  }

  async getCampaignEnrollments(campaignId: string): Promise<CampaignEnrollment[]> {
    return this.request(`/email-campaigns/${campaignId}/enrollments/`);
  }

  async getCampaignEnrollment(campaignId: string, enrollmentId: string): Promise<CampaignEnrollmentDetail> {
    return this.request(`/email-campaigns/${campaignId}/enrollments/${enrollmentId}/`);
  }

  async removeCampaignEnrollment(campaignId: string, enrollmentId: string): Promise<void> {
    return this.request(`/email-campaigns/${campaignId}/enrollments/${enrollmentId}/`, {
      method: 'DELETE',
    });
  }

  async getEmailCampaignStats(): Promise<EmailCampaignStats> {
    return this.request('/email-campaigns/stats/');
  }

  async syncInstantlyCampaigns(): Promise<{ success: boolean; synced: number; created: number }> {
    return this.request('/instantly/sync-campaigns/', {
      method: 'POST',
    });
  }

  async listInstantlyCampaigns(): Promise<InstantlyCampaign[]> {
    return this.request('/instantly/campaigns/');
  }

  // Local Pack Rankings & Geo Grid
  async getLocalPackRankings(clientId: string, params?: { days?: number; city?: string; keyword?: string }): Promise<LocalPackRankData> {
    const query = new URLSearchParams({ client: clientId });
    if (params?.days) query.set('days', String(params.days));
    if (params?.city) query.set('city', params.city);
    if (params?.keyword) query.set('keyword', params.keyword);
    return this.request(`/seo/local-pack-rankings/?${query}`);
  }

  async getGeoGrids(clientId: string, params?: { city?: string }): Promise<GeoGridSnapshot[]> {
    const query = new URLSearchParams({ client: clientId });
    if (params?.city) query.set('city', params.city);
    return this.request(`/seo/geo-grids/?${query}`);
  }

  async getGeoGrid(id: string): Promise<GeoGridSnapshot> {
    return this.request(`/seo/geo-grids/${id}/`);
  }

  async runGeoGrid(clientId: string, city: string, keyword: string): Promise<{ task_id: string }> {
    return this.request('/seo/geo-grids/run/', { method: 'POST', body: JSON.stringify({ client: clientId, city, keyword }) });
  }
}

// =============================================================================
// LINK TRACKING TYPES
// =============================================================================

export interface LinkClick {
  id: string;
  link: string;
  clicked_at: string;
  ip_address: string;
  user_agent: string;
  referrer: string;
  device_type: string;
  browser: string;
  os: string;
  country: string;
  city: string;
}

export interface TrackableLink {
  id: string;
  code: string;
  target_url: string;
  short_url: string;
  prospect: string | null;
  prospect_name: string;
  contact_email: string;
  contact_name: string;
  company_name: string;
  campaign: string;
  notes: string;
  created_by: string | null;
  created_at: string;
  click_count: number;
  first_clicked_at: string | null;
  last_clicked_at: string | null;
  recent_clicks: LinkClick[];
}

export interface TrackableLinkStats {
  total_links: number;
  total_clicks: number;
  links_with_clicks: number;
  click_rate: number;
  clicks_by_day: Array<{ date: string; count: number }>;
  top_links: TrackableLink[];
}

// =============================================================================
// PROSPECT CONTACT TYPES
// =============================================================================

export interface ProspectContact {
  id: string;
  prospect: string;
  prospect_name: string;
  first_name: string;
  last_name: string;
  full_name: string;
  title: string;
  title_category: string;
  email: string;
  phone: string;
  mobile: string;
  linkedin_url: string;
  is_decision_maker: boolean;
  is_primary: boolean;
  status: string;
  last_contacted_at: string | null;
  next_follow_up: string | null;
  source: string;
  confidence: string;
  notes: string;
  created_at: string;
  updated_at: string;
}

export interface ProspectContactStats {
  total: number;
  decision_makers: number;
  non_decision_makers: number;
  by_status: Record<string, number>;
  by_source: Record<string, number>;
  by_title_category: Record<string, number>;
}

// =============================================================================
// LEAD GENERATION TYPES
// =============================================================================

export interface LeadGenProperty {
  id: string;
  name: string;
  domain: string;
  vertical: string;
  is_active: boolean;
  lead_count: number;
  created_at: string;
}

export interface LeadGenLead {
  id: string;
  name: string;
  phone: string;
  email: string;
  message: string;
  service_requested: string;
  status: string;
  source: string;
  notes: string;
  property_name: string;
  property_domain: string;
  property_vertical: string;
  client_name: string;
  created_at: string;
  updated_at: string;
  acknowledged_at: string | null;
}

export interface LeadGenDashboardData {
  total_leads: number;
  leads_today: number;
  leads_this_week: number;
  leads_this_month: number;
  by_status: Record<string, number>;
  by_source: Record<string, number>;
  by_property: Array<{
    id: string;
    name: string;
    domain: string;
    vertical: string;
    count: number;
  }>;
  recent_leads: LeadGenLead[];
}

export interface LeadGenLeadsParams {
  property?: string;
  status?: string;
  date_from?: string;
  date_to?: string;
  search?: string;
  page?: number;
  page_size?: number;
}

// =============================================================================
// CONTACT SUBMISSIONS TYPES
// =============================================================================

export interface ContactSubmission {
  id: string;
  name: string;
  email: string;
  subject: string;
  message: string;
  status: 'new' | 'read' | 'replied' | 'archived' | 'spam';
  source_page: string;
  ip_address: string | null;
  user_agent: string;
  created_at: string;
  read_at: string | null;
  replied_at: string | null;
  internal_notes: string;
}

export interface ContactSubmissionStats {
  total: number;
  new: number;
  read: number;
  replied: number;
  archived: number;
  spam: number;
}

// =============================================================================
// COLD EMAIL CAMPAIGN TYPES
// =============================================================================

export interface EmailCampaign {
  id: string;
  name: string;
  instantly_campaign_id: string;
  status: 'draft' | 'active' | 'paused' | 'completed';
  description: string;
  created_by: string | null;
  created_by_name: string;
  total_leads: number;
  emails_sent: number;
  emails_opened: number;
  emails_clicked: number;
  emails_replied: number;
  emails_bounced: number;
  open_rate: number;
  reply_rate: number;
  click_rate: number;
  bounce_rate: number;
  created_at: string;
  updated_at: string;
}

export interface EmailEvent {
  id: string;
  event_type: 'sent' | 'opened' | 'clicked' | 'replied' | 'bounced' | 'unsubscribed';
  email_subject: string;
  link_url: string;
  reply_snippet: string;
  timestamp: string;
  created_at: string;
}

export interface CampaignEnrollment {
  id: string;
  campaign: string;
  contact: string;
  contact_email: string;
  contact_name: string;
  company_name: string;
  instantly_lead_id: string;
  status: 'pending' | 'active' | 'paused' | 'completed' | 'replied' | 'bounced' | 'unsubscribed';
  emails_sent: number;
  emails_opened: number;
  last_opened_at: string | null;
  emails_clicked: number;
  last_clicked_at: string | null;
  replied_at: string | null;
  bounced_at: string | null;
  enrolled_at: string;
}

export interface CampaignEnrollmentDetail extends CampaignEnrollment {
  events: EmailEvent[];
}

export interface EmailCampaignDetail extends EmailCampaign {
  enrollments: CampaignEnrollment[];
}

export interface CreateEmailCampaignRequest {
  name: string;
  description?: string;
  instantly_campaign_id: string;
}

export interface EmailCampaignStats {
  total_campaigns: number;
  active_campaigns: number;
  total_leads: number;
  total_sent: number;
  total_opened: number;
  total_clicked: number;
  total_replied: number;
  total_bounced: number;
  open_rate: number;
  reply_rate: number;
}

export interface InstantlyCampaign {
  id: string;
  name: string;
  status: string;
  is_linked: boolean;
}

export interface LocalPackRankData {
  cities: Record<string, {
    keywords: Record<string, {
      current_position: number | null;
      previous_position: number | null;
      change: number;
      history: Array<{ date: string; position: number | null }>;
    }>;
  }>;
  summary: {
    total_keywords_tracked: number;
    total_ranking: number;
    avg_position: number | null;
  };
}

export interface GeoGridSnapshot {
  id: string;
  keyword: string;
  date: string;
  grid_size: number;
  center_lat: number;
  center_lng: number;
  results: Array<{
    grid_row: number;
    grid_col: number;
    location_name: string;
    local_pack_position: number | null;
    local_pack_title: string | null;
    local_pack_rating: number | null;
    local_pack_reviews: number | null;
  }>;
  avg_position: number | null;
  visibility_pct: number | null;
  created_at: string;
}

export const api = new ApiService();
export default api;
